import { useState, useRef, useEffect, useCallback } from "react";
import { CheckCircle, AlertCircle, Clock, ChevronRight, ChevronLeft, TrendingUp, BookOpen, Users, Award, Stethoscope, BarChart2, Shield, Upload, Loader, Sparkles, RefreshCw, FileText, ChevronDown, ChevronUp, Trash2, Activity, Copy, Check, Download, X, ClipboardList, LogOut, Menu, User, CalendarClock, Calendar } from "lucide-react";
import { BUILD_BADGE } from "./buildInfo";
import { jsonrepair } from "jsonrepair";
import { supabase } from "./supabaseClient";
import AuthScreen from "./AuthScreen";
import DeadlinesView from "./components/DeadlinesView";
import ExpiryDatePicker from "./components/ExpiryDatePicker";
import { getUrgency, formatExpiryDate, computeReminderDates, findRenewalLink } from "./deadlineUtils";
import { EVIDENCE_VALIDITY, matchValidityRuleSync, addYearsToDate, getEvidenceAgeBadge } from "./evidenceValidity";
import { CredentiaIcon, CredentiaWordmark } from "./components/CredentiaLogo";

const API = process.env.REACT_APP_API_URL || "";

// Backend routes require the signed-in user's Supabase access token.
async function apiFetch(url, init = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = { ...(init.headers || {}) };
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return fetch(url, { ...init, headers });
}
if (typeof window !== "undefined") {
  window.__CREDENTIA_BUILD__ = BUILD_BADGE;
}

// ─── DOMAIN NUMBER MAP ────────────────────────────────────────────────────────
const DOMAIN_NUM = {
  // Anaesthetics
  cv: "CV", qualifications: "Quals.", work_exp: "WExp.", appraisals: "Appr.",
  dom1: "D1", dom2: "D2", dom3: "D3", dom4: "D4",
  dom5: "D5", dom6: "D6", dom7: "D7", dom8: "D8",
  dom9: "D9", dom10: "D10", dom11: "D11", dom12: "D12",
  dom13: "D13", dom14: "D14",
  practical: "Prac.", sia: "SIA", verification: "Verif.",
  // Emergency Medicine
  clinical: "D1", teaching: "D2", audit: "D3", research: "D4",
  management: "D5", communication: "D6", probity: "D7",
};

// ─── SPECIALTY DATA ───────────────────────────────────────────────────────────
export const SPECIALTIES = {
  anaesthetics: {
    id: "anaesthetics", name: "Anaesthetics",
    college: "Royal College of Anaesthetists (RCoA)",
    collegeShort: "RCoA", color: "#0ea5e9", icon: "🟢", iconImage: "/anesthesiology-icon.png",
    iconAttribution: {
      href: "https://www.flaticon.com/free-icons/anesthesiology",
      title: "anesthesiology icons",
      text: "Anesthesiology icons created by kliwir art - Flaticon",
    },
    description: "GMC CESR evidence requirements for Anaesthetics — 14 clinical domains plus foundation documents, mapped to the RCoA portfolio structure",
    cvPrompt: `You are a CESR assessor for the Royal College of Anaesthetists (RCoA) and GMC. Analyse this anaesthetist's CV against GMC CESR standards for all 14 domains. Respond ONLY with valid JSON — no markdown:\n{"overallScore":<0-100>,"overallRating":<"Strong"|"Adequate"|"Needs Work"|"Insufficient">,"summary":"<2-3 sentences>","sections":[{"id":"<string>","name":"<name>","status":<"present"|"weak"|"missing">,"finding":"<finding>","recommendation":"<advice>"}],"criticalGaps":["<gap>"],"gapsInService":["<specific unexplained gap with dates/duration where possible>"],"strengths":["<strength>"],"formatIssues":["<issue>"],"chronologyWarnings":["<warning>"]}\nSections: personal_details, primary_qualification, frca_primary, frca_final, training_history (flag gaps >3mo), current_post, general_anaesthesia, regional_anaesthesia, obstetric_anaesthesia, paediatric_anaesthesia, icu_exposure, pain_experience, resuscitation, teaching_training, audit_qi, research_publications, management_leadership, cpd, safeguarding, probity_appraisals, referees. Score: 85-100 Strong, 65-84 Adequate, 40-64 Needs Work, 0-39 Insufficient.`,
    domains: [
      { id: "cv", label: "Curriculum Vitae", icon: FileText, color: "#0ea5e9",
        description: "Full GMC-format CV — the foundation of your CESR application. AI analysis included.",
        items: [
          { id: "a_f1", label: "Curriculum Vitae", description: "Full GMC-format CV — personal details, GMC number, all posts chronological, qualifications, publications, teaching, audit, management, CPD, referees. No unexplained gaps >3 months.", accepts: "PDF or Word document", required: true, docType: "CV" },
        ]
      },
      { id: "qualifications", label: "Qualifications", icon: Award, color: "#8b5cf6",
        description: "Primary degree, postgraduate examinations, GMC registration, and language proficiency certificates",
        items: [
          { id: "a_f2", label: "Primary Medical Degree Certificate", description: "Original medical degree from country of qualification. Must show institution, degree title, and date of award.", accepts: "PDF/image of certificate", required: true, docType: "Certificate" },
          { id: "a_f3", label: "FRCA Primary Certificate", description: "RCoA FRCA Primary, or a recognised equivalent overseas fellowship examination accepted by the GMC.", accepts: "PDF/image of certificate", required: true, docType: "Certificate" },
          { id: "a_f4", label: "FRCA Final Certificate", description: "FRCA Final examination or recognised equivalent specialist qualification from a GMC-approved body.", accepts: "PDF/image of certificate", required: true, docType: "Certificate" },
          { id: "a_f5", label: "GMC Registration Certificate", description: "Current GMC certificate with licence to practise — download from GMC Online.", accepts: "GMC Online printout or certificate PDF", required: true, docType: "Certificate" },
          { id: "a_f6", label: "Additional Qualifications & Higher Degrees", description: "Higher degrees (MD, PhD, MSc), diplomas, or specialist certificates relevant to anaesthetic practice.", accepts: "Certificate or transcript PDF", required: false, docType: "Certificate" },
          { id: "a_f7", label: "English Language Proficiency", description: "Required if primary qualification obtained outside English-speaking country. IELTS Academic 7.5+ or OET grade B+ in all components.", accepts: "IELTS/OET certificate PDF", required: false, docType: "Certificate" },
        ]
      },
      { id: "work_exp", label: "Work Experience", icon: Stethoscope, color: "#0369a1",
        description: "Full chronological employment history with evidence of each post",
        items: [
          { id: "a_w1", label: "Complete Employment History — All Posts", description: "Chronological list of every post since graduation — trust/hospital name, grade, start and end dates, and responsibilities. No unexplained gaps >3 months.", accepts: "Table or structured document PDF", required: true, docType: "EmploymentHistory" },
          { id: "a_w2", label: "Appointment Letters / Contracts", description: "Formal appointment letters or contracts for each substantive post, confirming grade, employer, and dates.", accepts: "PDF of appointment letters — one per post", required: true, docType: "AppointmentLetter" },
          { id: "a_w3", label: "Current Post Job Plan", description: "Current job plan or employer confirmation of role, programmed activities, and anaesthetic responsibilities.", accepts: "Job plan PDF or employer letter", required: true, docType: "JobPlan" },
          { id: "a_w4", label: "Gap Explanation Letters", description: "For any period >3 months not in employment — career break, parental leave, illness, overseas travel. Signed explanation letter.", accepts: "Signed explanation letter PDF", required: false, docType: "GapExplanation" },
        ]
      },
      { id: "appraisals", label: "Appraisals & Revalidation", icon: Shield, color: "#7c3aed",
        description: "Annual NHS appraisals, revalidation documentation, and probity/health declarations",
        items: [
          { id: "a_ap1", label: "Annual Appraisal — Year 1 (most recent)", description: "Most recent completed NHS appraisal with satisfactory outcome. Must include PDP.", accepts: "Appraisal output PDF", required: true, docType: "Appraisal" },
          { id: "a_ap2", label: "Annual Appraisal — Year 2", description: "Second most recent appraisal with satisfactory outcome.", accepts: "Appraisal output PDF", required: true, docType: "Appraisal" },
          { id: "a_ap3", label: "Annual Appraisal — Year 3", description: "Third most recent appraisal.", accepts: "Appraisal output PDF", required: true, docType: "Appraisal" },
          { id: "a_ap4", label: "Annual Appraisal — Year 4", description: "Fourth most recent appraisal.", accepts: "Appraisal output PDF", required: true, docType: "Appraisal" },
          { id: "a_ap5", label: "Annual Appraisal — Year 5 (oldest)", description: "Oldest in the 5-year set — completes the required run.", accepts: "Appraisal output PDF", required: true, docType: "Appraisal" },
          { id: "a_ap6", label: "Revalidation Documentation", description: "Evidence of successful revalidation if due — responsible officer confirmation letter or GMC Online confirmation.", accepts: "Revalidation letter or GMC Online confirmation PDF", required: false, docType: "Revalidation" },
          { id: "a_ap7", label: "Health & Probity Declaration", description: "Signed declaration of good health and probity — no conditions on GMC registration, no active FTP investigations.", accepts: "Signed declaration PDF", required: true, docType: "Declaration" },
          { id: "a_ap8", label: "Indemnity Evidence", description: "Current medical defence/indemnity membership — MDU, MPS, MDDUS, or NHS indemnity employer confirmation.", accepts: "Membership certificate or employer indemnity letter", required: true, docType: "Indemnity" },
        ]
      },
      { id: "dom1", label: "DOM 1 — Behavioural & Communication", icon: Users, color: "#2FAF9F",
        description: "Communication skills, patient interaction, reflective practice, and professional behaviour",
        items: [
          { id: "a_d1a", label: "Multi-source Feedback (MSF / 360°)", description: "Formal 360° feedback from consultants, trainees, nurses, ODPs, and patients. GMC revalidation MSF acceptable. Must span multiple professional groups.", accepts: "MSF summary report PDF", required: true, docType: "MSF" },
          { id: "a_d1b", label: "Patient Feedback", description: "Formal patient satisfaction data — Friends & Family results, patient survey outcomes, or written testimonials.", accepts: "Survey report PDF or trust data extract", required: true, docType: "PatientFeedback" },
          { id: "a_d1c", label: "Reflective Practice Entries", description: "Structured reflective entries demonstrating learning from clinical experience, critical incidents, complaints, or significant events.", accepts: "Reflection log PDF (anonymised)", required: true, docType: "Reflection" },
          { id: "a_d1d", label: "Complaints & Compliments Record", description: "Record of any complaints and their resolution. Positive feedback letters. A nil complaint record signed by clinical director is acceptable.", accepts: "Trust HR letter or clinical director statement PDF", required: true, docType: "ComplaintsRecord" },
          { id: "a_d1e", label: "Communication Training Evidence", description: "Formal communication skills training — breaking bad news, consent training, cultural competency, or equivalent CPD.", accepts: "Certificate or CPD diary entry", required: false, docType: "Certificate" },
        ]
      },
      { id: "dom2", label: "DOM 2 — Medical Professionalism & Regulation", icon: Shield, color: "#0891b2",
        description: "Professional standards, GMC Good Medical Practice, CPD, and regulatory compliance",
        items: [
          { id: "a_d2a", label: "CPD Record — RCoA Portfolio (all years)", description: "Minimum 50 CPD credits per year — external (conferences, courses), internal (departmental teaching, audit meetings), and personal (e-learning, reading). RCoA CPD diary export preferred.", accepts: "RCoA CPD diary export PDF or CPD log with certificates", required: true, docType: "CPDRecord" },
          { id: "a_d2b", label: "RCoA Membership Certificate (FRCA)", description: "Fellow of the Royal College of Anaesthetists — membership certificate or confirmation letter.", accepts: "Membership certificate or RCoA letter", required: true, docType: "Certificate" },
          { id: "a_d2c", label: "Personal Development Plan", description: "PDP from appraisal outputs, identifying learning goals and evidencing how they were met across each year.", accepts: "PDP extract from appraisal output PDF", required: true, docType: "PDP" },
          { id: "a_d2d", label: "Probity Statement", description: "Declaration of compliance with GMC Good Medical Practice — no dishonesty, no criminal convictions, no regulatory sanctions.", accepts: "Signed personal statement or solicitor declaration", required: true, docType: "Declaration" },
        ]
      },
      { id: "dom3", label: "DOM 3 — Team Working", icon: Users, color: "#059669",
        description: "MDT participation, theatre team leadership, and collaborative working evidence",
        items: [
          { id: "a_d3a", label: "MDT Participation Records", description: "Evidence of attending and contributing to MDTs — pre-assessment MDT, ICU ward rounds, pain MDT, tumour board. Minutes, rota, or supervisor letter.", accepts: "Meeting minutes extract, rota, or supervisor letter", required: true, docType: "MDTRecord" },
          { id: "a_d3b", label: "Theatre Team Leadership Evidence", description: "Evidence of leading the anaesthetic team — briefings, WHO surgical safety checklist leadership, communication in complex cases.", accepts: "Supervisor letter, simulation record, or reflective entry", required: true, docType: "TeamLeadership" },
          { id: "a_d3c", label: "Simulation Team Exercise Records", description: "Crisis resource management (CRM), team briefing exercises, or in-situ simulation participation.", accepts: "Simulation centre record, certificate, or debrief summary", required: false, docType: "SimulationRecord" },
          { id: "a_d3d", label: "Peer References from Consultant Colleagues", description: "Letters from at least 2 consultant colleagues confirming team working ability, clinical competence, and professional conduct.", accepts: "Structured reference letter PDF", required: true, docType: "Reference" },
        ]
      },
      { id: "dom4", label: "DOM 4 — Safety & Quality Improvement", icon: BarChart2, color: "#d97706",
        description: "Clinical audit cycles, QI projects, incident reporting, and patient safety contributions",
        items: [
          { id: "a_d4a", label: "Completed Audit Cycle with Re-audit", description: "Full audit loop: standard set → data collected → change implemented → re-audit showing improvement. Must demonstrate a closed loop.", accepts: "Full audit report PDF", required: true, docType: "AuditReport" },
          { id: "a_d4b", label: "Second Audit or QI Project", description: "A second audit or QI project (PDSA cycles) demonstrating ongoing engagement with quality improvement.", accepts: "Audit report or QI project summary PDF", required: true, docType: "AuditReport" },
          { id: "a_d4c", label: "National Audit Participation", description: "Contribution to RCoA/NCEPOD national audits — NAP projects (NAP4-7), ACSA standards, or NCEPOD reports.", accepts: "Participation certificate, data submission, or department letter", required: false, docType: "AuditParticipation" },
          { id: "a_d4d", label: "M&M / Governance Contribution", description: "Participation in morbidity and mortality meetings, serious incident reviews, or departmental clinical governance.", accepts: "Meeting records (anonymised), reflection, or governance letter", required: true, docType: "GovernanceRecord" },
          { id: "a_d4e", label: "Incident Reporting Record", description: "Evidence of reporting patient safety incidents through Datix, NRLS/LFPSE, or equivalent — demonstrating safety culture engagement.", accepts: "Screenshot, supervisor letter, or governance confirmation", required: false, docType: "IncidentRecord" },
        ]
      },
      { id: "dom5", label: "DOM 5 — Safeguarding", icon: Shield, color: "#dc2626",
        description: "Safeguarding children and vulnerable adults training, DBS, and clinical involvement",
        items: [
          { id: "a_d5a", label: "Safeguarding Children — Level 3 Certificate", description: "Level 3 safeguarding children training, current within 3 years. Minimum 4 hours. Required for all anaesthetists.", accepts: "Training certificate PDF", required: true, docType: "Certificate" },
          { id: "a_d5b", label: "Safeguarding Adults — Level 3 Certificate", description: "Level 3 safeguarding vulnerable adults training, current within 3 years.", accepts: "Training certificate PDF", required: true, docType: "Certificate" },
          { id: "a_d5c", label: "Enhanced DBS Certificate", description: "Enhanced DBS check — must be current or registered with the DBS Update Service.", accepts: "DBS certificate PDF or Update Service confirmation", required: true, docType: "DBS" },
          { id: "a_d5d", label: "Mental Capacity Act & DoLS Training", description: "MCA 2005 and Deprivation of Liberty Safeguards training — relevant to consent in anaesthesia for incapacitous patients.", accepts: "Training certificate or e-learning completion record", required: true, docType: "Certificate" },
          { id: "a_d5e", label: "Safeguarding Case Involvement", description: "Anonymised evidence of involvement in a safeguarding referral or case discussion — reflective entry or departmental letter.", accepts: "Anonymised reflective entry or supervisor letter", required: false, docType: "Reflection" },
        ]
      },
      { id: "dom6", label: "DOM 6 — Education & Training", icon: BookOpen, color: "#7c3aed",
        description: "Teaching delivered, trainee supervision, educational qualifications, and learner feedback",
        items: [
          { id: "a_d6a", label: "Teaching Log — All Sessions", description: "Full log of teaching delivered — tutorials, simulation, ward teaching, lectures. Date, topic, audience grade and number, format, and duration for each.", accepts: "Teaching log spreadsheet or PDF", required: true, docType: "TeachingLog" },
          { id: "a_d6b", label: "Trainee Supervision Records", description: "Educational or clinical supervisor evidence — ePortfolio records, deanery letters, or trainee feedback confirming supervisory responsibilities.", accepts: "ePortfolio screenshot, deanery letter, or trainee evaluation", required: true, docType: "SupervisionRecord" },
          { id: "a_d6c", label: "Teaching Qualification", description: "PGCert Medical Education, PGCME, or equivalent. Significantly strengthens the application.", accepts: "Certificate or transcript PDF", required: false, docType: "Certificate" },
          { id: "a_d6d", label: "Simulation Faculty / Instructor Role", description: "Designing or facilitating simulation, acting as faculty on courses, or ALS/ILS/ATLS instructor status.", accepts: "Course faculty record, instructor certificate, or letter", required: false, docType: "Certificate" },
          { id: "a_d6e", label: "Learner Feedback on Teaching", description: "Structured feedback from trainees, students, or course participants — evaluation forms, ratings, or supervisor of training reports.", accepts: "Evaluation form summary or feedback report PDF", required: true, docType: "TeachingFeedback" },
        ]
      },
      { id: "dom7", label: "DOM 7 — Research & Data Management", icon: Award, color: "#0369a1",
        description: "Published research, conference work, GCP certification, and data governance",
        items: [
          { id: "a_d7a", label: "Peer-reviewed Publications", description: "Papers in peer-reviewed journals — original research, case reports, systematic reviews, or editorials. Include journal, year, volume/page, and authorship position.", accepts: "PDF of paper or PubMed printout", required: false, docType: "Publication" },
          { id: "a_d7b", label: "Conference Presentations or Posters", description: "Regional, national, or international conference abstract with evidence of acceptance and presentation — RCoA, AAGBI, ESA, or equivalent.", accepts: "Abstract PDF, poster PDF, or acceptance email", required: true, docType: "ConferencePoster" },
          { id: "a_d7c", label: "GCP / Research Governance Certificate", description: "Good Clinical Practice training certificate. Required if involved in any clinical trials or research studies.", accepts: "GCP certificate PDF", required: false, docType: "Certificate" },
          { id: "a_d7d", label: "Research Involvement Evidence", description: "PI, co-investigator, or recruiting site involvement in a research study — R&D approval or ethics letter.", accepts: "R&D approval, ethics approval, or trial confirmation PDF", required: false, docType: "ResearchEvidence" },
          { id: "a_d7e", label: "Data Protection & Governance Training", description: "GDPR/data protection training completion — mandatory for handling patient data in research or audit.", accepts: "E-learning completion certificate or trust training record", required: true, docType: "Certificate" },
        ]
      },
      { id: "dom8", label: "DOM 8 — Peri-operative Medicine & Health Promotion", icon: Activity, color: "#0891b2",
        description: "Pre-assessment, enhanced recovery, patient optimisation, and health promotion activities",
        items: [
          { id: "a_d8a", label: "Pre-operative Assessment Clinic Involvement", description: "Evidence of participating in or leading pre-op assessment clinics — optimisation, risk stratification, shared decision-making, pre-op workup.", accepts: "Clinic rota, supervisor letter, or logbook extract", required: true, docType: "ClinicalEvidence" },
          { id: "a_d8b", label: "Enhanced Recovery After Surgery (ERAS) Evidence", description: "Involvement in an ERAS programme — multimodal analgesia, PONV prophylaxis, fluid management protocols, or ERAS committee contribution.", accepts: "ERAS protocol, supervisor letter, or committee records", required: false, docType: "ClinicalEvidence" },
          { id: "a_d8c", label: "High-risk Patient Optimisation", description: "Managing high-risk patients pre-operatively — CPET referral, cardiology/respiratory liaison, shared decision-making documentation.", accepts: "Case summaries (anonymised), clinic letters, or supervisor letter", required: false, docType: "CaseSummary" },
          { id: "a_d8d", label: "Consent for Anaesthesia Evidence", description: "Patient-centred consent for anaesthesia — covering risks, options, and alternatives. Reflective entry or audit of consent practice.", accepts: "Reflection, consent audit, or supervisor sign-off", required: true, docType: "ConsentEvidence" },
          { id: "a_d8e", label: "Health Promotion Activity", description: "Promoting patient health — smoking cessation advice, alcohol reduction, weight optimisation referrals, or relevant patient educational material.", accepts: "Personal statement, patient leaflet, or supervisor letter", required: false, docType: "HealthPromotion" },
        ]
      },
      { id: "dom9", label: "DOM 9 — General Anaesthesia", icon: Stethoscope, color: "#0ea5e9",
        description: "Breadth and complexity of general anaesthetic practice across patient groups and case types",
        items: [
          { id: "a_d9a", label: "Anaesthetic Logbook — GA Overview", description: "Logbook showing breadth of GA practice — total cases, ASA grade distribution, surgical specialty mix, emergency vs elective ratio. RCoA recommends 3000+ cases.", accepts: "Logbook export PDF or structured case summary", required: true, docType: "Logbook" },
          { id: "a_d9b", label: "High-risk & Complex GA Cases", description: "Managing high-risk patients — ASA 3/4, emergency laparotomy, one-lung ventilation, neuroanaesthesia, or cardiac patients for non-cardiac surgery.", accepts: "Logbook extract, case list, or supervisor letter", required: true, docType: "Logbook" },
          { id: "a_d9c", label: "Airway Management Evidence", description: "Managing difficult airways — DAS-guideline approach, awake fibreoptic intubation (AFOI), video laryngoscopy, surgical airway. DOPS or supervisor sign-off.", accepts: "DOPS forms, logbook section, or supervisor letter", required: true, docType: "WBAs" },
          { id: "a_d9d", label: "TIVA & Target-controlled Infusion Evidence", description: "TIVA experience — propofol TCI, remifentanil infusions, TIVA for high PONV-risk, NMBDs with monitoring.", accepts: "Logbook section or supervisor letter", required: true, docType: "ClinicalEvidence" },
          { id: "a_d9e", label: "WBAs for General Anaesthesia", description: "Completed CEX, CBD, DOPS for GA cases demonstrating consultant-equivalent performance.", accepts: "WBA forms PDF or ePortfolio export", required: true, docType: "WBAs" },
          { id: "a_d9f", label: "GA Supervisor Confirmation Letter", description: "Named consultant letter confirming GA competency at consultant level, commenting on case complexity and independent practice.", accepts: "Signed consultant letter on trust headed paper", required: true, docType: "SupervisorLetter" },
        ]
      },
      { id: "dom10", label: "DOM 10 — Regional Anaesthesia", icon: Activity, color: "#7c3aed",
        description: "Neuraxial and peripheral nerve block experience, ultrasound guidance, and RA competency",
        items: [
          { id: "a_d10a", label: "Regional Anaesthesia Logbook", description: "RA procedures performed — spinals, epidurals, CSE, peripheral nerve blocks. Case numbers, procedure types, supervised vs independent practice.", accepts: "Logbook export or RA procedure list PDF", required: true, docType: "Logbook" },
          { id: "a_d10b", label: "Peripheral Nerve Block Evidence", description: "Upper and lower limb blocks — brachial plexus (ISB, axillary, supraclavicular), femoral, sciatic, adductor canal, TAP. Ultrasound-guided preferred.", accepts: "Logbook section, DOPS, or supervisor letter", required: true, docType: "ClinicalEvidence" },
          { id: "a_d10c", label: "Neuraxial Block Evidence", description: "Spinal, epidural, and CSE across surgical contexts. Managing complications — inadequate block, dural tap, post-dural puncture headache.", accepts: "Logbook section or supervisor letter", required: true, docType: "ClinicalEvidence" },
          { id: "a_d10d", label: "Ultrasound-guided RA DOPS", description: "DOPS assessments for ultrasound-guided nerve blocks confirming real-time US visualisation competency.", accepts: "DOPS forms PDF", required: true, docType: "WBAs" },
          { id: "a_d10e", label: "RA Supervisor Confirmation Letter", description: "Letter from RA lead or named consultant confirming RA competency and breadth of techniques performed.", accepts: "Signed consultant letter on trust headed paper", required: true, docType: "SupervisorLetter" },
        ]
      },
      { id: "dom11", label: "DOM 11 — Resuscitation & Transfer", icon: Activity, color: "#dc2626",
        description: "Life support certification, critical care transfers, and resuscitation leadership",
        items: [
          { id: "a_d11a", label: "Advanced Life Support (ALS) Certificate", description: "Current ALS provider certificate — valid 4 years. ALS instructor status is a significant strength.", accepts: "ALS certificate PDF (check expiry date)", required: true, docType: "Certificate" },
          { id: "a_d11b", label: "Paediatric Life Support Certificate", description: "APLS or EPALS provider. Required if you anaesthetise children. PALS acceptable if paediatrics is a minor part of practice.", accepts: "APLS/EPALS/PALS certificate PDF", required: false, docType: "Certificate" },
          { id: "a_d11c", label: "Difficult Airway / CICO Management Evidence", description: "Competency in can't-intubate-can't-oxygenate scenarios — DAS guidelines, surgical airway, or simulation training.", accepts: "Simulation record, DAS training certificate, or supervisor letter", required: true, docType: "ClinicalEvidence" },
          { id: "a_d11d", label: "Critical Care Transfer Evidence", description: "Transferring critically ill patients — inter-hospital transfer, theatre to ICU, or retrieval. Transfer documentation or supervisor letter.", accepts: "Transfer records (anonymised), logbook, or supervisor letter", required: true, docType: "ClinicalEvidence" },
          { id: "a_d11e", label: "Resuscitation Leadership Evidence", description: "Leading resuscitation calls — cardiac arrest team leadership, peri-arrest management, post-ROSC care.", accepts: "Resus records (anonymised), supervisor letter, or reflective entry", required: false, docType: "ClinicalEvidence" },
        ]
      },
      { id: "dom12", label: "DOM 12 — Sedation", icon: Stethoscope, color: "#0891b2",
        description: "Procedural sedation, conscious sedation, and monitoring competency outside theatre",
        items: [
          { id: "a_d12a", label: "Procedural Sedation Logbook", description: "Sedation cases — endoscopy, radiology, cardiology (DC cardioversion), or dental. Monitoring used and sedation depth achieved documented.", accepts: "Logbook section or sedation case list PDF", required: true, docType: "Logbook" },
          { id: "a_d12b", label: "Conscious Sedation Evidence", description: "Providing conscious sedation where the patient remains responsive — minor procedures or diagnostic sedation. Demonstrates understanding of sedation continuum.", accepts: "Logbook section or supervisor letter", required: true, docType: "ClinicalEvidence" },
          { id: "a_d12c", label: "Sedation Outside Theatre Evidence", description: "Anaesthetist-led sedation in remote locations — endoscopy suite, MRI/CT scanner, catheter lab. Managing the challenges of remote environments.", accepts: "Logbook extract, rota, or supervisor letter", required: true, docType: "ClinicalEvidence" },
          { id: "a_d12d", label: "Sedation Protocol / Guidelines Contribution", description: "Contributing to or implementing a sedation protocol, delivering sedation training, or completing a sedation safety audit.", accepts: "Protocol document, audit, or supervisor letter", required: false, docType: "ClinicalEvidence" },
        ]
      },
      { id: "dom13", label: "DOM 13 — Pain", icon: AlertCircle, color: "#d97706",
        description: "Acute and chronic pain management, regional analgesic techniques, and pain service involvement",
        items: [
          { id: "a_d13a", label: "Acute Pain Service Involvement", description: "Participating in or running an acute pain service — ward rounds, epidural monitoring, PCA reviews, post-operative pain assessment. Rota or supervisor letter.", accepts: "Rota extract, supervisor letter, or acute pain log", required: true, docType: "ClinicalEvidence" },
          { id: "a_d13b", label: "Chronic Pain Clinic Evidence", description: "Attending or contributing to a chronic pain clinic — history-taking, interventional procedures, MDT, or pain psychology liaison.", accepts: "Clinic logbook, supervisor letter, or rota evidence", required: false, docType: "ClinicalEvidence" },
          { id: "a_d13c", label: "Interventional Pain Procedure Evidence", description: "Pain procedures performed — epidural steroid injections, facet joint blocks, trigger point injections, coeliac plexus blocks.", accepts: "Procedure logbook or supervisor letter", required: false, docType: "Logbook" },
          { id: "a_d13d", label: "Multimodal Analgesia & ERAS Evidence", description: "Multimodal analgesic prescribing — paracetamol, NSAIDs, regional techniques, gabapentinoids, opioid stewardship. Audit or case-based evidence.", accepts: "Audit, ERAS protocol, or CBD reflecting multimodal approach", required: true, docType: "ClinicalEvidence" },
          { id: "a_d13e", label: "Pain Supervisor Confirmation Letter", description: "Letter from acute or chronic pain lead confirming pain management competency and involvement.", accepts: "Signed consultant letter on trust headed paper", required: false, docType: "SupervisorLetter" },
        ]
      },
      { id: "dom14", label: "DOM 14 — Intensive Care Medicine", icon: Activity, color: "#059669",
        description: "ICU/HDU attachment, critical care competencies, and level 2/3 patient management",
        items: [
          { id: "a_d14a", label: "ICU/HDU Attachment Evidence", description: "Formal ICU or HDU attachment — dates, institution, grade, and supervising consultant. Appointment letter, rota, or deanery confirmation.", accepts: "Appointment letter, rota, or deanery confirmation PDF", required: true, docType: "AppointmentLetter" },
          { id: "a_d14b", label: "Critical Care Logbook / Competency Record", description: "Log of ICU patients managed — ventilation, vasopressors, renal replacement, invasive monitoring. Competency sign-offs from ICU consultant.", accepts: "ICU logbook, competency form, or ePortfolio export", required: true, docType: "Logbook" },
          { id: "a_d14c", label: "Mechanical Ventilation Evidence", description: "Managing invasively ventilated patients — lung-protective ventilation, weaning protocols, NIV, high-flow oxygen. DOPS or supervisor sign-off.", accepts: "DOPS, logbook extract, or supervisor letter", required: true, docType: "WBAs" },
          { id: "a_d14d", label: "Vasopressor & Haemodynamic Management", description: "Managing haemodynamic instability — noradrenaline, adrenaline, vasopressin, inotropes, cardiac output monitoring.", accepts: "Logbook section, CBD, or supervisor letter", required: true, docType: "ClinicalEvidence" },
          { id: "a_d14e", label: "Sepsis Management Evidence", description: "Managing sepsis and septic shock to NICE/Sepsis-6 standards — bundle compliance, source control, antibiotic stewardship.", accepts: "Audit data, case-based discussion, or supervisor letter", required: false, docType: "ClinicalEvidence" },
          { id: "a_d14f", label: "ICU Supervisor Confirmation Letter", description: "ICU consultant letter confirming critical care competency and level of independent practice achieved.", accepts: "Signed consultant letter on trust headed paper", required: true, docType: "SupervisorLetter" },
        ]
      },
      { id: "practical", label: "Practical Procedures", icon: TrendingUp, color: "#0369a1",
        description: "Documented competency in core anaesthetic procedural skills",
        items: [
          { id: "a_pr1", label: "Arterial Line Insertion DOPS", description: "DOPS for radial and/or femoral arterial line insertion, including waveform interpretation and troubleshooting.", accepts: "DOPS form PDF", required: true, docType: "DOPS" },
          { id: "a_pr2", label: "Central Venous Access DOPS", description: "DOPS for central venous catheter — IJV, subclavian, or femoral. Ultrasound-guided preferred. Includes CXR interpretation.", accepts: "DOPS form PDF", required: true, docType: "DOPS" },
          { id: "a_pr3", label: "Fibreoptic / Video Laryngoscopy Competency", description: "Awake fibreoptic intubation (AFOI), video laryngoscopy (McGrath, C-MAC, GlideScope). DOPS or logbook entries.", accepts: "DOPS form, logbook section, or supervisor sign-off", required: true, docType: "DOPS" },
          { id: "a_pr4", label: "Thoracic Epidural & Spinal DOPS", description: "DOPS for thoracic epidurals, spinals across approaches, and CSE technique.", accepts: "DOPS forms or logbook section PDF", required: true, docType: "DOPS" },
          { id: "a_pr5", label: "Peripheral Nerve Block DOPS (3+ types)", description: "Specific DOPS assessments for peripheral nerve blocks — at least 3 different block types documented.", accepts: "DOPS forms PDF", required: true, docType: "DOPS" },
          { id: "a_pr6", label: "Intraosseous Access", description: "IO access competency (EZ-IO or FAST1). Simulation training acceptable if clinical cases are rare.", accepts: "DOPS, simulation record, or ALS refresher confirmation", required: false, docType: "DOPS" },
          { id: "a_pr7", label: "Surgical Airway / Cricothyroidotomy", description: "Emergency surgical airway — scalpel-bougie-tube technique per DAS guidelines. Simulation training acceptable.", accepts: "Simulation record, DAS training certificate, or supervisor letter", required: true, docType: "Certificate" },
          { id: "a_pr8", label: "Transoesophageal Echocardiography (TOE)", description: "Basic or advanced TOE if performing cardiac or high-risk surgery. BSE accreditation or documented supervised sessions.", accepts: "BSE certificate, logbook, or supervisor sign-off", required: false, docType: "Certificate" },
        ]
      },
      { id: "sia", label: "Special Interest Areas", icon: Award, color: "#7c3aed",
        description: "Obstetric anaesthesia, regional sub-specialisation, and major general surgery anaesthesia",
        items: [
          { id: "a_si1", label: "Obstetric Anaesthesia Logbook", description: "Dedicated obstetric logbook — epidurals for labour, spinals/CSE for LSCS, GA for emergency LSCS, obstetric haemorrhage, pre-eclampsia. Case numbers and supervised vs independent practice.", accepts: "Obstetric anaesthesia logbook export or case list PDF", required: true, docType: "Logbook" },
          { id: "a_si2", label: "Obstetric Anaesthesia Supervisor Letter", description: "Letter from the lead obstetric anaesthetist confirming competency including emergency management.", accepts: "Signed consultant letter on trust headed paper", required: true, docType: "SupervisorLetter" },
          { id: "a_si3", label: "Paediatric Anaesthesia Logbook", description: "Paediatric logbook — age range covered (including neonates if applicable), case types, complexity, supervised vs independent.", accepts: "Paediatric logbook export or age-stratified case list PDF", required: true, docType: "Logbook" },
          { id: "a_si4", label: "Paediatric Anaesthesia Supervisor Letter", description: "Letter from the paediatric anaesthesia lead confirming competency across the age range you have practised in.", accepts: "Signed consultant letter on trust headed paper", required: true, docType: "SupervisorLetter" },
          { id: "a_si5", label: "Major General Surgery Anaesthesia Evidence", description: "Anaesthesia for major general surgery — colorectal, hepatobiliary, oesophagogastric, laparoscopic, or emergency laparotomy.", accepts: "Logbook section, case list, or supervisor letter", required: true, docType: "ClinicalEvidence" },
          { id: "a_si6", label: "Additional Sub-specialty Evidence", description: "Any additional sub-specialty — neuroanaesthesia, cardiac, thoracic, vascular, head & neck, or trauma. Logbook, rota, or supervisor letter.", accepts: "Logbook section or supervisor letter", required: false, docType: "ClinicalEvidence" },
        ]
      },
      { id: "verification", label: "Verification Pro Formas", icon: CheckCircle, color: "#059669",
        description: "GMC and RCoA verification forms completed by named supervisors",
        items: [
          { id: "a_v1", label: "Primary Clinical Supervisor Verification", description: "Completed GMC/RCoA verification pro forma from your primary clinical supervisor, confirming they have worked with you and can attest to your evidence.", accepts: "Signed verification pro forma PDF", required: true, docType: "VerificationForm" },
          { id: "a_v2", label: "Second Consultant Verification", description: "Verification from a second named consultant who can confirm your clinical competency independently.", accepts: "Signed verification pro forma PDF", required: true, docType: "VerificationForm" },
          { id: "a_v3", label: "Clinical Director / Medical Director Verification", description: "Verification from your clinical director or medical director confirming employment, good standing, and absence of disciplinary proceedings.", accepts: "Signed verification pro forma or employer letter PDF", required: true, docType: "VerificationForm" },
          { id: "a_v4", label: "Domain-specific Supervisor Verifications", description: "Verification from specialty leads — obstetric lead, ICU consultant, pain lead, paediatric lead — confirming domain-specific competency.", accepts: "Signed verification pro formas PDF — one per domain lead", required: true, docType: "VerificationForm" },
          { id: "a_v5", label: "Responsible Officer Statement", description: "Statement from your Responsible Officer confirming satisfactory appraisals, no outstanding concerns, and recommendation for revalidation.", accepts: "RO statement letter PDF", required: true, docType: "VerificationForm" },
        ]
      },
    ],
  },

  emergency_medicine: {
    id: "emergency_medicine", name: "Emergency Medicine", college: "Royal College of Emergency Medicine (RCEM)",
    collegeShort: "RCEM", color: "#ef4444", icon: "🚨", iconImage: "/emergency-medicine-icon.png",
    iconAttribution: {
      href: "https://www.flaticon.com/free-icons/emergency",
      title: "emergency icons",
      text: "Emergency icons created by kliwir art - Flaticon",
    },
    description: "CESR evidence requirements for Emergency Medicine as defined by the RCEM and GMC",
    cvPrompt: `You are a CESR assessor for the Royal College of Emergency Medicine (RCEM) and GMC. Analyse this emergency physician's CV against GMC CESR standards. Respond ONLY with valid JSON — no markdown:\n{"overallScore":<0-100>,"overallRating":<"Strong"|"Adequate"|"Needs Work"|"Insufficient">,"summary":"<2-3 sentences>","sections":[{"id":"<string>","name":"<name>","status":<"present"|"weak"|"missing">,"finding":"<finding>","recommendation":"<advice>"}],"criticalGaps":["<gap>"],"gapsInService":["<specific unexplained gap with dates/duration where possible>"],"strengths":["<strength>"],"formatIssues":["<issue>"],"chronologyWarnings":["<warning>"]}\nSections (use these ids): personal_details, primary_qualification, postgrad_qualifications (FRCEM Primary & Final or equivalent), training_history (flag gaps >3mo), current_post, clinical_experience (paed EM/trauma/resus room/procedures/toxicology/psychiatric), teaching (simulation valued), audit_qi (RCEM national audit/4hr/SDEC), research_publications, management_leadership (EPRR/MIMMS), cpd (RCEM 50hr/yr), prizes_distinctions, referees. Score: 85-100 Strong, 65-84 Adequate, 40-64 Needs Work, 0-39 Insufficient.`,
    domains: [
      { id: "clinical", label: "Clinical Skills & Knowledge", icon: Activity, color: "#ef4444", description: "Qualifications, logbooks, procedural competencies, and consultant-equivalent EM practice", items: [
        { id: "em_c1", label: "FRCEM Primary Certificate", description: "Or equivalent — MRCEM Part A or overseas equivalent.", accepts: "PDF/image of certificate", required: true, docType: "Certificate" },
        { id: "em_c2", label: "FRCEM Final Certificate", description: "FRCEM Final Written and OSCE, or equivalent specialist examination.", accepts: "PDF/image of certificate", required: true, docType: "Certificate" },
        { id: "em_c3", label: "Emergency Medicine Logbook", description: "Breadth of EM case experience — medical, surgical, psychiatric, and paediatric emergencies across all acuity levels.", accepts: "Logbook export, RCEM ePortfolio extract, or case summary", required: true, docType: "Logbook" },
        { id: "em_c4", label: "Paediatric Emergency Medicine Evidence", description: "Paediatric EM exposure including neonatal emergencies, APLS-level management, and safeguarding.", accepts: "Logbook section, APLS certificate, or supervisor letter", required: true, docType: "Evidence" },
        { id: "em_c5", label: "Resuscitation & Life Support Certificates", description: "Current ALS (mandatory), APLS or EPALS, and ATLS or equivalent trauma course.", accepts: "ALS/APLS/ATLS certificates (must be in date)", required: true, docType: "Certificate" },
        { id: "em_c6", label: "Major Trauma Management Evidence", description: "Experience managing major trauma — team leadership, ATLS principles, documentation of cases.", accepts: "Logbook section, trauma call records, or supervisor letter", required: true, docType: "Evidence" },
        { id: "em_c7", label: "Procedural Competencies", description: "RSI/intubation, chest drain insertion, central venous access, IO access, FAST/eFAST, arterial lines, cardioversion.", accepts: "DOPS forms, procedural logbook, or supervisor sign-off", required: true, docType: "WBAs" },
        { id: "em_c8", label: "Resuscitation Room / High Acuity Evidence", description: "Evidence of leading the resuscitation room — cardiac arrests, major trauma, STEMI, stroke, sepsis.", accepts: "Supervisor letter, case log extract, or rota evidence", required: true, docType: "Evidence" },
        { id: "em_c9", label: "Toxicology & Psychiatric Emergency Exposure", description: "Management of poisoning, overdose, and psychiatric emergencies in the ED.", accepts: "Logbook section, supervisor letter, or reflective entry", required: false, docType: "Evidence" },
        { id: "em_c10", label: "Supervised Practice at Consultant Level", description: "Evidence of working at consultant-equivalent level — shop floor leadership, unsupervised senior cover.", accepts: "Referee/supervisor letter or trust confirmation", required: true, docType: "SupervisorLetter" },
        { id: "em_c11", label: "Workplace Based Assessments (WBAs)", description: "Mini-CEX, CBD, and DOPS across a range of EM presentations demonstrating consultant-equivalent performance.", accepts: "PDF of completed WBA forms or ePortfolio export", required: true, docType: "WBAs" },
      ]},
      { id: "teaching", label: "Teaching & Training", icon: BookOpen, color: "#2FAF9F", description: "Simulation, bedside teaching, trainee supervision, and life support instruction", items: [
        { id: "em_t1", label: "Teaching Sessions Log", description: "Record of teaching — simulation scenarios, resus room teaching, tutorials. Simulation is particularly valued in EM.", accepts: "Teaching log, simulation scenario record, or course programme", required: true, docType: "TeachingLog" },
        { id: "em_t2", label: "Trainee / Student Supervision Records", description: "Formal supervision of FY, CMT, ST trainees or medical students.", accepts: "Supervisor reports, ePortfolio records, or HR confirmation", required: true, docType: "SupervisionRecord" },
        { id: "em_t3", label: "Life Support Instructor Status", description: "ALS, APLS, ATLS, or ALSO instructor — running or faculty on life support courses.", accepts: "Instructor certificate or course faculty records", required: false, docType: "Certificate" },
        { id: "em_t4", label: "Teaching Qualification", description: "PG Cert in Medical Education, PGCME, or equivalent.", accepts: "Certificate or transcript", required: false, docType: "Certificate" },
        { id: "em_t5", label: "Learner Feedback / Evaluation", description: "Structured feedback from trainees, students, or course participants.", accepts: "Feedback forms or summary evaluation report", required: false, docType: "Feedback" },
      ]},
      { id: "audit", label: "Audit & Quality Improvement", icon: BarChart2, color: "#f59e0b", description: "Clinical audit cycles, QI projects, and ED performance improvement", items: [
        { id: "em_a1", label: "Completed Audit Cycle with Re-audit", description: "Full audit loop with re-audit demonstrating improvement. RCEM national audit participation counts.", accepts: "Audit report PDF or RCEM audit submission", required: true, docType: "AuditReport" },
        { id: "em_a2", label: "QI Project Evidence", description: "QI project relevant to EM — 4-hour standard, sepsis pathway, SDEC implementation, streaming redesign.", accepts: "QI project report or summary", required: true, docType: "QIReport" },
        { id: "em_a3", label: "RCEM National Audit Participation", description: "Contribution to RCEM national audits — data collection, local lead, or report authorship.", accepts: "RCEM audit report, participation letter, or data submission confirmation", required: false, docType: "AuditReport" },
        { id: "em_a4", label: "Governance / M&M Contribution", description: "M&M meetings, serious incident reviews, clinical governance, complaints analysis.", accepts: "Meeting minutes, reflection, or departmental letter", required: false, docType: "Reflection" },
      ]},
      { id: "research", label: "Research & Publications", icon: Award, color: "#10b981", description: "Research, publications, and academic contributions to emergency medicine", items: [
        { id: "em_r1", label: "Peer-reviewed Publications", description: "Published papers — EM journals (EMJ, EMA, Annals of EM) carry particular weight.", accepts: "PDF of paper or PubMed printout", required: false, docType: "Publication" },
        { id: "em_r2", label: "Conference Presentations or Posters", description: "RCEM Annual Scientific Conference, ICEM, or equivalent regional/national conference.", accepts: "Abstract, poster PDF, or acceptance email", required: true, docType: "Presentation" },
        { id: "em_r3", label: "GCP / Research Governance Certificate", description: "Good Clinical Practice training or equivalent.", accepts: "Certificate PDF", required: false, docType: "Certificate" },
      ]},
      { id: "management", label: "Management & Leadership", icon: TrendingUp, color: "#f43f5e", description: "Leadership roles, major incident planning, and ED service improvement", items: [
        { id: "em_m1", label: "Lead Role or Committee Membership", description: "Clinical lead within ED, governance lead, college tutor, EPRR lead, rota coordinator.", accepts: "Appointment letter, job plan, or departmental letter", required: true, docType: "AppointmentLetter" },
        { id: "em_m2", label: "Major Incident / EPRR Involvement", description: "EPRR training, MIMMS/JESIP, table-top exercise participation, or real incident response.", accepts: "MIMMS certificate, EPRR training records, or incident report", required: false, docType: "Certificate" },
        { id: "em_m3", label: "Leadership / Management Training", description: "NHS Leadership Academy, RCEM leadership course, MMS, FMLM membership.", accepts: "Certificate or course completion confirmation", required: false, docType: "Certificate" },
        { id: "em_m4", label: "Service Improvement / Development", description: "Initiating or materially improving an ED service — SDEC pathway, triage redesign, rapid assessment unit.", accepts: "Report, letter, data showing impact, or trust documentation", required: false, docType: "Report" },
      ]},
      { id: "communication", label: "Communication & Teamwork", icon: Users, color: "#06b6d4", description: "Multidisciplinary teamwork, handover quality, and patient communication", items: [
        { id: "em_co1", label: "Multi-source Feedback (MSF / 360°)", description: "Formal 360° feedback from consultants, trainees, nursing, paramedic, and admin staff.", accepts: "MSF summary report PDF", required: true, docType: "MSFReport" },
        { id: "em_co2", label: "MDT / Trauma Team Participation Records", description: "Evidence of trauma team leadership, handover processes (SBAR, safety huddles), specialty referrals.", accepts: "Trauma call records, rota evidence, or departmental letter", required: true, docType: "MDTRecord" },
        { id: "em_co3", label: "Safeguarding Training & Involvement", description: "Level 3 safeguarding children and adults training. Evidence of involvement in safeguarding referrals.", accepts: "Training certificate and/or departmental letter", required: true, docType: "Certificate" },
        { id: "em_co4", label: "Patient Feedback", description: "Friends & Family Test results, patient satisfaction survey outcomes, or patient testimonials.", accepts: "Survey results, trust report, or individual feedback", required: false, docType: "PatientFeedback" },
      ]},
      { id: "probity", label: "Probity & Professional Standing", icon: Shield, color: "#8aabcc", description: "Professional behaviour, appraisals, RCEM CPD, and GMC standing", items: [
        { id: "em_p1", label: "Annual Appraisal Documents (last 5 years)", description: "Completed NHS appraisal forms with outputs for all 5 preceding years.", accepts: "Appraisal output PDF (×5)", required: true, docType: "Appraisal" },
        { id: "em_p2", label: "CPD Record (RCEM)", description: "Minimum 50 CPD hours/year. RCEM CPD diary or ePortfolio export preferred.", accepts: "RCEM CPD diary export or CPD log with supporting certificates", required: true, docType: "CPDRecord" },
        { id: "em_p3", label: "GMC Registration Certificate / Revalidation", description: "Current GMC registration with licence to practise.", accepts: "GMC certificate or online registration printout", required: true, docType: "GMCCertificate" },
        { id: "em_p4", label: "Health & Probity Statement", description: "Signed declaration of no current fitness-to-practise investigations.", accepts: "Signed statement or GMC confirmation letter", required: true, docType: "Statement" },
      ]},
    ],
  },
};

function renderSpecialtyIcon(sp, size = 18) {
  if (sp.iconImage) {
    return (
      <img
        src={sp.iconImage}
        alt={`${sp.name} icon`}
        style={{ width: size, height: size, objectFit: "contain", display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}
      />
    );
  }
  return <span style={{ fontSize: size, lineHeight: 1 }}>{sp.icon}</span>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function useLSState(key, init) {
  const [s, ss] = useState(() => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : init; } catch { return init; } });
  const set = v => { ss(v); try { localStorage.setItem(key, JSON.stringify(v)); } catch {} };
  return [s, set];
}
export const initEvidence = sp => { const e = {}; SPECIALTIES[sp].domains.forEach(d => d.items.forEach(i => { e[i.id] = { status: "missing", notes: "", analysis: null, cvAnalysis: null, fileName: "", suggestedFilename: "" }; })); return e; };
const toBase64 = file => new Promise((res, rej) => { const r = new FileReader(); r.onload = e => res(e.target.result.split(",")[1]); r.onerror = () => rej(new Error("Read failed")); r.readAsDataURL(file); });

const parseAiJson = (rawText) => {
  const cleaned = (rawText || "").replace(/```json|```/g, "").trim();
  const first = cleaned.indexOf("{");
  const last = cleaned.lastIndexOf("}");
  const candidate = first !== -1 && last !== -1 ? cleaned.substring(first, last + 1) : cleaned;
  try {
    return JSON.parse(candidate);
  } catch {
    return JSON.parse(jsonrepair(candidate));
  }
};
const mediaTy = f => {
  const mime = (f?.type || "").toLowerCase();
  const name = (f?.name || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";

  const officeByExt = {
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ppt: "application/vnd.ms-powerpoint",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  };

  if (mime === "application/pdf") return "application/pdf";
  if (mime.startsWith("image/")) return mime;
  if (officeByExt[ext]) return officeByExt[ext];
  return null;
};
const scoreColor = s => s === "meets" ? "#10b981" : s === "partial" ? "#f59e0b" : "#ef4444";
const scoreBg = s => s === "meets" ? "rgba(16,185,129,.1)" : s === "partial" ? "rgba(245,158,11,.1)" : "rgba(239,68,68,.1)";
const scoreLabel = s => s === "meets" ? "Meets Requirement" : s === "partial" ? "Partially Meets" : "Does Not Meet";
const cvScoreCol = s => s >= 85 ? "#10b981" : s >= 65 ? "#f59e0b" : s >= 40 ? "#f97316" : "#ef4444";
const today = () => new Date().toISOString().slice(0, 10);

const tokenize = txt => (txt || "")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, " ")
  .split(" ")
  .filter(Boolean);

const buildItemCandidates = specialtyId => {
  const sp = SPECIALTIES[specialtyId];
  return sp.domains.flatMap(d => d.items.map(i => ({ ...i, domainId: d.id })));
};

const scoreFileToItem = (fileName, item) => {
  const base = (fileName || "").replace(/\.[^.]+$/, "");
  const fileTokens = tokenize(base);
  const hay = `${item.label} ${item.description} ${item.docType} ${item.accepts}`.toLowerCase();
  let score = 0;
  fileTokens.forEach(tok => {
    if (tok.length > 2 && hay.includes(tok)) score += tok.length > 5 ? 3 : 2;
  });
  const has = w => fileTokens.includes(w);
  const label = item.label.toLowerCase();
  if (has("cv") && item.docType === "CV") score += 7;
  if (has("frca") && label.includes("frca")) score += 6;
  if (has("final") && label.includes("final")) score += 6;
  if (has("primary") && label.includes("primary")) score += 5;
  if (has("gmc") && label.includes("gmc")) score += 5;
  if (has("appraisal") && item.docType === "Appraisal") score += 5;
  if ((has("dbs") || has("safeguarding")) && (label.includes("dbs") || label.includes("safeguarding"))) score += 5;
  return score;
};

// Finds the best-matching evidence item for a file, ignoring completion status
const findTopMatch = (candidates, fileName) => {
  const ranked = candidates
    .map(item => ({ item, score: scoreFileToItem(fileName, item) }))
    .sort((a, b) => b.score - a.score);
  return ranked[0]?.score >= 4 ? ranked[0].item : null;
};

const analyseEvidenceFile = async ({ specialtyId, domainId, item, file }) => {
  const sp = SPECIALTIES[specialtyId];
  const domNum = DOMAIN_NUM[domainId] || "D?";
  const mt = mediaTy(file);
  if (!mt) throw new Error("Please upload a PDF, image, Word, Excel, or PowerPoint file.");
  const now = new Date();
  const monthYear = now.toLocaleString("en-GB", { month: "short" }) + now.getFullYear();
  const filenameDomNum = domNum.replace(/\./g, "");

  // Primary evidence verdict analysis (OpenAI) — also extracts evidenceDate
  const res = await apiFetch(`${API}/api/openai/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      system: `You are a CESR assessor for the ${sp.college}. Evaluate described evidence for ${sp.name}. Return ONLY valid JSON:\n{"verdict":"meets"|"partial"|"does_not_meet","confidence":0-100,"summary":"<1-2 sentences>","strengths":["<strength>"],"gaps":["<gap>"],"recommendation":"<specific action>","suggestedFilename":"<${filenameDomNum}_${item.docType}_${monthYear}_ShortDesc>","evidenceDate":"<YYYY-MM-DD date of the clinical activity, logbook entry, MSF, audit, WBA, or document date — infer from filename if present, otherwise null>","evidenceDateConfidence":"<exact|approximate|inferred|unknown>","evidenceDateNote":"<one sentence on how date was determined, or null>"}`,
      input: `Assess this uploaded file against:\nRequirement: ${item.label}\nDescription: ${item.description}\nExpected: ${item.accepts}\nMandatory: ${item.required ? "Yes" : "No"}\nDomain: ${domNum}\nDocType: ${item.docType}\nFilename: ${file.name}\nMedia type: ${mt}\n\nDoes it satisfy the CESR ${sp.name} requirement? Also suggest a GMC-compliant filename following the format: Domain_DocType_MonYear_BriefDescription (e.g. D6_TeachingLog_May2026_SimulationFaculty). Also extract evidenceDate from the filename or document type context.`,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error?.message || "API error");
  const parsed = parseAiJson(data.output_text || "");

  // Secondary expiry extraction (Claude claude-sonnet-4-6) — non-fatal, best-effort
  let expiryData = null;
  try {
    const expiryRes = await apiFetch(`${API}/api/anthropic/messages`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-6",
        max_tokens: 512,
        system: `You are a UK medical certificate expiry specialist for CESR portfolio evidence.
Given information about a certificate or training document uploaded by a doctor, determine whether it has a known expiry or renewal period.

Return ONLY valid JSON with NO extra text:
{
  "expiryDate": "<YYYY-MM-DD based on standard renewal periods, or null if genuinely unknown>",
  "expiryType": "<certificate|training|appraisal|membership|registration|null>",
  "expiryLabel": "<human readable label e.g. 'ALS Certificate expiry', or null>",
  "renewalLeadTime": <integer weeks recommended for renewal booking, or null>,
  "confidence": <0-100 — how confident you are in the expiry date>
}

Known UK renewal periods (use today's date as issue date only if no date is in the filename):
- ALS certificate: 4 years
- APLS certificate: 4 years
- ATLS certificate: valid while registered with RCS
- Safeguarding Children Level 3: 3 years
- Safeguarding Adults: 3 years
- DBS check: 3 years (recommended)
- GMC registration: annual renewal
- GCP training: 2 years
- Annual appraisal: 1 year
- Indemnity membership: 1 year
If the document type has no standard expiry, return null for expiryDate.`,
        messages: [{
          role: "user",
          content: `Document information:\nRequirement: ${item.label}\nDescription: ${item.description}\nDocument type: ${item.docType}\nFilename: ${file.name}\nMedia type: ${mt}\nUpload date: ${now.toISOString().split("T")[0]}\n\nDoes this document type have a standard expiry date? If so, estimate the expiry date based on the upload date and standard renewal periods.`,
        }],
      }),
    });
    if (expiryRes.ok) {
      const expiryJson = await expiryRes.json();
      const rawText = expiryJson.content?.[0]?.text || "";
      const expParsed = parseAiJson(rawText);
      if (expParsed?.expiryDate && expParsed.expiryDate !== "null" && expParsed.expiryDate !== null) {
        expiryData = {
          expiryDate: expParsed.expiryDate,
          expiryType: expParsed.expiryType || null,
          expiryLabel: expParsed.expiryLabel || null,
          renewalLeadTime: expParsed.renewalLeadTime || null,
          confidence: expParsed.confidence || null,
        };
      }
    }
  } catch { /* expiry extraction is non-fatal */ }

  // Validity rule matching — sync first, then gpt-4o-mini fallback if no match
  let matchedValidityRule = matchValidityRuleSync(item.docType, item.label);
  let validityData = matchedValidityRule ? EVIDENCE_VALIDITY[matchedValidityRule] : null;

  if (!matchedValidityRule) {
    try {
      const classifyRes = await apiFetch(`${API}/api/openai`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "gpt-4o-mini",
          max_tokens: 30,
          messages: [
            {
              role: "system",
              content: `You are classifying medical evidence documents for a CESR portfolio tracker. Given a document type and label, return the single best matching key from this list, or the word null if none match: ${Object.keys(EVIDENCE_VALIDITY).join(", ")}. Return only the key string, nothing else.`,
            },
            {
              role: "user",
              content: `docType: ${item.docType}, label: ${item.label}`,
            },
          ],
        }),
      });
      if (classifyRes.ok) {
        const classifyJson = await classifyRes.json();
        const key = (classifyJson.choices?.[0]?.message?.content || "").trim().replace(/['"]/g, "");
        if (key && key !== "null" && EVIDENCE_VALIDITY[key]) {
          matchedValidityRule = key;
          validityData = EVIDENCE_VALIDITY[key];
        }
      }
    } catch { /* non-fatal */ }
  }

  return {
    status: parsed.verdict === "meets" ? "complete" : parsed.verdict === "partial" ? "partial" : "missing",
    analysis: {
      ...parsed,
      evidenceDate: (parsed.evidenceDate && parsed.evidenceDate !== "null") ? parsed.evidenceDate : null,
      evidenceDateConfidence: parsed.evidenceDateConfidence || "unknown",
      evidenceDateNote: parsed.evidenceDateNote || null,
    },
    fileName: file.name,
    suggestedFilename: parsed.suggestedFilename || "",
    expiryData,
    matchedValidityRule,
    validityData,
  };
};

const UI_THEMES = {
  clinical_light: {
    label: "Light",
    appBg: "#F8FBFF", appText: "#1F2D3D",
    sidebarBg: "#F2F7FC", sidebarBorder: "#DCE7F2",
    panelBg: "#FFFFFF", panelBorder: "#E2E8F0",
    cardBg: "#FFFFFF", cardBorder: "#E2E8F0",
    muted: "#5F738A", dim: "#94A3B8",
    navText: "#5F738A", navHoverBg: "rgba(15,23,42,.06)", navHoverText: "#1F2D3D", navActiveBg: "rgba(15,23,42,.1)", navActiveText: "#0F172A",
    selectBg: "#FFFFFF", selectBorder: "#E2E8F0",
  },
  midnight_indigo: {
    label: "Dark",
    appBg: "#111827", appText: "#F3F7FF",
    sidebarBg: "#1A2233", sidebarBorder: "#2A354F",
    panelBg: "#232F45", panelBorder: "#2A3A58",
    cardBg: "#0F1F2E", cardBorder: "#224068",
    muted: "#9FB0C7", dim: "#6F829E",
    navText: "#9FB0C7", navHoverBg: "rgba(255,255,255,.03)", navHoverText: "#d2dceb", navActiveBg: "rgba(255,255,255,.06)", navActiveText: "#ffffff",
    selectBg: "#162e4a", selectBorder: "#224068",
  },
};

function CopyBtn({ text, small }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(text).catch(() => {}); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <button onClick={copy} title="Copy to clipboard"
      style={{ padding: small ? "2px 7px" : "4px 10px", borderRadius: 6, border: "1px solid #213045", background: copied ? "rgba(16,185,129,.12)" : "rgba(255,255,255,.04)", color: copied ? "#10b981" : "#7a9ab8", cursor: "pointer", fontFamily: "inherit", fontSize: 10, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 3, transition: "all .15s" }}>
      {copied ? <><Check size={9} />Copied</> : <><Copy size={9} />Copy</>}
    </button>
  );
}

// ─── Submission Pack Generator ────────────────────────────────────────────────
function generateHTML(sp, evidence, userDetails, narratives) {
  const spData = SPECIALTIES[sp];
  const allItems = spData.domains.flatMap(d => d.items);
  const complete = allItems.filter(i => evidence[i.id]?.status === "complete").length;
  const partial  = allItems.filter(i => evidence[i.id]?.status === "partial").length;
  const missing  = allItems.filter(i => evidence[i.id]?.status === "missing" && i.required).length;

  const evidenceRows = spData.domains.flatMap(d =>
    d.items.map(item => {
      const ev = evidence[item.id] || {};
      const status = ev.status === "complete" ? "✓ Complete" : ev.status === "partial" ? "⚠ Partial" : "✗ Missing";
      const statusColor = ev.status === "complete" ? "#16a34a" : ev.status === "partial" ? "#d97706" : "#dc2626";
      const fn = ev.suggestedFilename || ev.fileName || "—";
      const summary = ev.analysis?.summary || "No evidence uploaded";
      return `<tr><td style="color:#374151;font-size:11px;padding:6px 10px;border-bottom:1px solid #f3f4f6;">${DOMAIN_NUM[d.id]}</td><td style="padding:6px 10px;border-bottom:1px solid #f3f4f6;font-size:11px;">${item.label}${item.required ? ' <span style="color:#dc2626;font-size:9px;font-weight:700;">REQ</span>' : ""}</td><td style="padding:6px 10px;border-bottom:1px solid #f3f4f6;font-size:10px;color:#6b7280;font-family:monospace;">${fn}</td><td style="padding:6px 10px;border-bottom:1px solid #f3f4f6;font-size:11px;color:${statusColor};font-weight:600;">${status}</td><td style="padding:6px 10px;border-bottom:1px solid #f3f4f6;font-size:10px;color:#6b7280;">${summary}</td></tr>`;
    })
  ).join("");

  const domainSections = spData.domains.map(d => {
    const narrative = narratives[d.id] || "Evidence for this domain is being compiled.";
    const items = d.items.filter(i => evidence[i.id]?.status === "complete" || evidence[i.id]?.status === "partial");
    const cited = items.map(i => `<li style="font-size:11px;color:#374151;margin-bottom:3px;">${evidence[i.id]?.suggestedFilename || evidence[i.id]?.fileName || i.label}</li>`).join("");
    return `
      <div style="margin-bottom:36px;page-break-inside:avoid;">
        <h3 style="font-size:15px;font-weight:700;color:#172235;margin:0 0 6px;padding-bottom:6px;border-bottom:2px solid #e5e7eb;">${DOMAIN_NUM[d.id]} — ${d.label}</h3>
        <p style="font-size:12px;color:#374151;line-height:1.7;margin:0 0 10px;">${narrative}</p>
        ${cited ? `<div style="background:#f9fafb;border-radius:6px;padding:10px 14px;"><div style="font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:.06em;margin-bottom:6px;">Evidence Cited</div><ul style="margin:0;padding-left:16px;">${cited}</ul></div>` : ""}
      </div>`;
  }).join("");

  const gapRows = spData.domains.flatMap(d => d.items.filter(i => evidence[i.id]?.status !== "complete")).map(item => {
    const ev = evidence[item.id] || {};
    const rec = ev.analysis?.recommendation || "Upload evidence and request AI analysis for specific recommendations.";
    return `<tr><td style="padding:6px 10px;border-bottom:1px solid #f3f4f6;font-size:11px;">${item.label}</td><td style="padding:6px 10px;border-bottom:1px solid #f3f4f6;font-size:11px;color:${ev.status === "partial" ? "#d97706" : "#dc2626"};font-weight:600;">${ev.status === "partial" ? "Partial" : "Missing"}</td><td style="padding:6px 10px;border-bottom:1px solid #f3f4f6;font-size:10px;color:#6b7280;">${item.required ? "Yes" : "No"}</td><td style="padding:6px 10px;border-bottom:1px solid #f3f4f6;font-size:11px;color:#374151;">${rec}</td></tr>`;
  }).join("");

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>CESR Submission Pack — ${spData.name}</title>
<style>
  body{font-family:Arial,sans-serif;color:#172235;margin:0;padding:0;background:#fff;}
  @media print{body{font-size:11px;}.no-print{display:none!important;}@page{margin:2cm;size:A4;}}
  .page{max-width:900px;margin:0 auto;padding:40px;}
  h1{font-size:24px;font-weight:700;color:#172235;margin:0 0 4px;}
  h2{font-size:17px;font-weight:700;color:#172235;margin:32px 0 14px;padding:8px 0;border-bottom:3px solid #172235;}
  table{width:100%;border-collapse:collapse;margin-bottom:16px;}
  th{background:#172235;color:#fff;padding:7px 10px;font-size:11px;text-align:left;font-weight:600;}
  .badge{display:inline-block;padding:2px 8px;border-radius:20px;font-size:10px;font-weight:700;}
  .complete{background:#dcfce7;color:#16a34a;} .partial{background:#fef9c3;color:#ca8a04;} .missing{background:#fee2e2;color:#dc2626;}
</style></head><body><div class="page">

<div style="border-bottom:3px solid #172235;padding-bottom:20px;margin-bottom:28px;">
  <div style="font-size:10px;font-weight:700;color:#6b7280;letter-spacing:.1em;text-transform:uppercase;margin-bottom:6px;">${spData.college}</div>
  <h1>Credentia CESR Submission Pack</h1>
  <div style="font-size:16px;color:#374151;margin-top:4px;">${spData.name}</div>
  <div style="display:flex;gap:32px;margin-top:20px;flex-wrap:wrap;">
    <div><div style="font-size:10px;color:#9ca3af;text-transform:uppercase;font-weight:700;margin-bottom:2px;">Doctor</div><div style="font-size:13px;font-weight:600;">${userDetails.name || "—"}</div></div>
    <div><div style="font-size:10px;color:#9ca3af;text-transform:uppercase;font-weight:700;margin-bottom:2px;">GMC Number</div><div style="font-size:13px;font-weight:600;">${userDetails.gmc || "—"}</div></div>
    <div><div style="font-size:10px;color:#9ca3af;text-transform:uppercase;font-weight:700;margin-bottom:2px;">Date Prepared</div><div style="font-size:13px;font-weight:600;">${userDetails.date || today()}</div></div>
    <div><div style="font-size:10px;color:#9ca3af;text-transform:uppercase;font-weight:700;margin-bottom:2px;">Portfolio Status</div><div style="font-size:13px;font-weight:600;">${complete} complete · ${partial} partial · ${missing} required missing</div></div>
  </div>
</div>

<h2>Section 1 — Probity Declaration</h2>
<div style="background:#f9fafb;border-radius:8px;padding:16px 20px;font-size:12px;color:#374151;line-height:1.7;">
  I, <strong>${userDetails.name || "[Name]"}</strong>, GMC number <strong>${userDetails.gmc || "[GMC Number]"}</strong>, declare that the evidence submitted in this CESR portfolio is accurate, complete, and genuine. I understand that providing false or misleading information to the GMC may constitute a serious breach of Good Medical Practice and could affect my registration.
  <div style="margin-top:16px;display:flex;gap:48px;">
    <div><div style="font-size:10px;color:#9ca3af;margin-bottom:20px;">Signature</div><div style="border-bottom:1px solid #172235;width:200px;"></div></div>
    <div><div style="font-size:10px;color:#9ca3af;margin-bottom:20px;">Date</div><div style="border-bottom:1px solid #172235;width:140px;"></div></div>
  </div>
</div>

<h2>Section 2 — Evidence Index</h2>
<p style="font-size:12px;color:#6b7280;margin-bottom:12px;">All evidence documents are named using the convention: [Domain]_[DocType]_[MonYear]_[Description] (e.g. D6_TeachingLog_May2026_SimulationFaculty). Files should be submitted in corresponding domain folders.</p>
<table>
  <thead><tr><th style="width:50px;">Domain</th><th style="width:28%;">Evidence Item</th><th style="width:26%;">Filename</th><th style="width:90px;">Status</th><th>AI Analysis Summary</th></tr></thead>
  <tbody>${evidenceRows}</tbody>
</table>

<div style="page-break-before:always;"></div>
<h2>Section 3 — Domain Narratives</h2>
<p style="font-size:12px;color:#6b7280;margin-bottom:20px;">The following narratives explain how the submitted evidence demonstrates competency against each GMC/RCoA domain. Evidence documents are referenced by their submission filenames.</p>
${domainSections}

<div style="page-break-before:always;"></div>
<h2>Section 4 — Gap Report & Action Plan</h2>
${gapRows ? `<p style="font-size:12px;color:#6b7280;margin-bottom:12px;">The following items require further evidence before submission. Mandatory items must be resolved before the application can be submitted.</p>
<table>
  <thead><tr><th>Evidence Item</th><th style="width:90px;">Status</th><th style="width:70px;">Mandatory</th><th>Recommended Action</th></tr></thead>
  <tbody>${gapRows}</tbody>
</table>` : `<div style="background:#f0fdf4;border-radius:8px;padding:16px 20px;color:#16a34a;font-size:13px;font-weight:600;">✓ All evidence items are complete. Portfolio is ready for submission review.</div>`}

<div style="margin-top:40px;padding-top:16px;border-top:1px solid #e5e7eb;font-size:10px;color:#9ca3af;text-align:center;">
  Generated by Credentia · ${today()} · ${spData.college} Guidance · GMC CESR Standards
</div>
</div></body></html>`;
}

// ─── Submission Pack Modal ────────────────────────────────────────────────────
function SubmissionPackModal({ specialty, evidence, onClose, user }) {
  const sp = SPECIALTIES[specialty];
  const [step, setStep] = useState("details"); // details | generating | preview
  const [userDetails, setUserDetails] = useState({
    name: user?.user_metadata?.full_name || "",
    gmc:  user?.user_metadata?.gmc_number || "",
    date: today(),
  });
  const [narratives, setNarratives] = useState({});
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");

  const buildNarrative = (d, domNum) => {
    const items = d.items;
    const complete = items.filter(i => evidence[i.id]?.status === "complete");
    const partial  = items.filter(i => evidence[i.id]?.status === "partial");
    const missing  = items.filter(i => evidence[i.id]?.status === "missing" && i.required);

    const parts = [];

    parts.push(
      `In support of my CESR application for ${sp.name}, I present the following evidence for ${domNum} — ${d.label}.`
    );

    if (complete.length > 0) {
      const fileRefs = complete.map(i => {
        const ev = evidence[i.id] || {};
        return ev.suggestedFilename ? `${i.label} (${ev.suggestedFilename})` : i.label;
      });
      parts.push(`I have provided complete documentation for: ${fileRefs.join("; ")}.`);

      // Add analysis summaries from uploaded evidence
      complete.forEach(i => {
        const ev = evidence[i.id] || {};
        if (ev.analysis?.summary) parts.push(ev.analysis.summary);
        if (ev.analysis?.strengths?.length > 0) {
          parts.push(`Key strengths of this evidence include: ${ev.analysis.strengths.slice(0,2).join("; ")}.`);
        }
      });
    }

    if (partial.length > 0) {
      const pList = partial.map(i => i.label).join("; ");
      parts.push(
        `The following evidence is currently in progress and will be provided in full prior to submission: ${pList}.`
      );
      partial.forEach(i => {
        const ev = evidence[i.id] || {};
        if (ev.analysis?.recommendation) parts.push(ev.analysis.recommendation);
      });
    }

    if (missing.length > 0) {
      const mList = missing.map(i => i.label).join("; ");
      parts.push(
        `The following mandatory items are outstanding and are being actively compiled: ${mList}. These will be included in the final submission.`
      );
    }

    if (complete.length === 0 && partial.length === 0) {
      parts.push(
        `Evidence for this domain is currently being compiled. All required documentation will be provided in the complete submission package.`
      );
    }

    parts.push(
      `I am satisfied that, taken together, this evidence demonstrates my competency in ${d.label} to the standard required for specialist registration in ${sp.name} under GMC CESR criteria.`
    );

    return parts.join(" ");
  };

  const [aiMode, setAiMode] = useState(true); // try AI first
  const [narrSource, setNarrSource] = useState({}); // track per-domain: "ai" | "template"
  const [domainsDone, setDomainsDone] = useState([]);

  const callNarrative = async (d, domNum) => {
    const items = d.items.map(i => {
      const ev = evidence[i.id] || {};
      const parts = [`${i.label}: ${ev.status || "not yet uploaded"}`];
      if (ev.analysis?.summary) parts.push(ev.analysis.summary);
      if (ev.suggestedFilename) parts.push(`File: ${ev.suggestedFilename}`);
      return parts.join(". ");
    }).join("\n");

    const res = await apiFetch(`${API}/api/openai/chat`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        system: "Write concise, professional GMC CESR narratives.",
        input:
          `Write a 160-200 word first-person CESR portfolio narrative for the GMC for domain ${domNum} — ${d.label} in ${sp.name}. ` +
          `Reference specific evidence items and filenames. Acknowledge gaps professionally. Flowing prose only, no headers or bullets.\n\nEvidence:\n${items}\n\nWrite only the narrative, no title.`,
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error?.message || `HTTP ${res.status}`);
    const text = data.output_text?.trim();
    if (!text) throw new Error("Empty response");
    return text;
  };

  const generate = async () => {
    setStep("generating"); setError(""); setDomainsDone([]);
    const narrMap = {};
    const sourceMap = {};

    for (let i = 0; i < sp.domains.length; i++) {
      const d = sp.domains[i];
      const domNum = `D${i + 1}`;
      setProgress(`${domNum} — ${d.label}`);
      if (aiMode) {
        try {
          narrMap[d.id] = await callNarrative(d, domNum);
          sourceMap[d.id] = "ai";
        } catch {
          narrMap[d.id] = buildNarrative(d, domNum);
          sourceMap[d.id] = "template";
        }
      } else {
        narrMap[d.id] = buildNarrative(d, domNum);
        sourceMap[d.id] = "template";
      }
      setDomainsDone(prev => [...prev, d.id]);
    }

    setNarratives(narrMap);
    setNarrSource(sourceMap);
    setProgress("Done!");
    setTimeout(() => setStep("preview"), 300);
  };

  const [copied, setCopied] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  const downloadPDF = async () => {
    setPdfGenerating(true);
    try {
      const html = generateHTML(specialty, evidence, userDetails, narratives);

      // Render the HTML in a hidden same-origin iframe so html2canvas can access it
      const iframe = document.createElement("iframe");
      iframe.style.cssText = "position:fixed;left:-9999px;top:0;width:900px;height:1px;border:none;visibility:hidden;";
      document.body.appendChild(iframe);

      await new Promise(resolve => {
        iframe.onload = resolve;
        iframe.srcdoc = html;
      });

      // Give scripts/fonts a moment to settle
      await new Promise(r => setTimeout(r, 400));

      const iDoc = iframe.contentDocument || iframe.contentWindow.document;
      const rootEl = iDoc.querySelector(".page") || iDoc.body;

      const { default: jsPDF } = await import("jspdf");
      const { default: html2canvas } = await import("html2canvas");

      const A4_W_MM = 210;
      const A4_H_MM = 297;
      const MARGIN_MM = 14;
      const contentW_MM = A4_W_MM - MARGIN_MM * 2;

      // Scale so 900px maps to contentW_MM in the PDF
      const scale = 2; // retina quality
      const canvas = await html2canvas(rootEl, {
        scale,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        windowWidth: 900,
      });

      document.body.removeChild(iframe);

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

      const pxPerMm = (canvas.width / scale) / contentW_MM;
      const totalH_MM = (canvas.height / scale) / pxPerMm;

      let yOffset = 0;
      let pageNum = 0;

      while (yOffset < totalH_MM) {
        if (pageNum > 0) pdf.addPage();
        const sliceH_MM = Math.min(A4_H_MM - MARGIN_MM * 2, totalH_MM - yOffset);
        // Crop the canvas slice via a temporary canvas
        const srcY = Math.round(yOffset * pxPerMm * scale);
        const srcH = Math.round(sliceH_MM * pxPerMm * scale);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = srcH;
        const ctx = sliceCanvas.getContext("2d");
        ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.92);
        pdf.addImage(sliceData, "JPEG", MARGIN_MM, MARGIN_MM, contentW_MM, sliceH_MM);
        yOffset += sliceH_MM;
        pageNum++;
      }

      const docName = userDetails.name
        ? `CESR_SubmissionPack_${userDetails.name.replace(/\s+/g, "_")}.pdf`
        : `CESR_SubmissionPack_${sp.name.replace(/\s+/g, "_")}.pdf`;
      pdf.save(docName);
    } catch (err) {
      console.error("PDF generation failed:", err);
      // Graceful fallback: download as HTML
      const html = generateHTML(specialty, evidence, userDetails, narratives);
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "CESR_SubmissionPack.html";
      a.click(); URL.revokeObjectURL(url);
    } finally {
      setPdfGenerating(false);
    }
  };

  const copyHTML = () => {
    const html = generateHTML(specialty, evidence, userDetails, narratives);
    navigator.clipboard?.writeText(html).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2500);
    }).catch(() => {
      // Fallback: textarea select
      const ta = document.createElement("textarea");
      ta.value = html; document.body.appendChild(ta);
      ta.select(); document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true); setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.8)", backdropFilter: "blur(6px)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#162e4a", border: "1px solid #213045", borderRadius: 16, padding: 28, width: "100%", maxWidth: 520 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 18, fontWeight: 700, color: "#fff" }}>Generate Submission Pack</div>
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>{sp.name} · {sp.collegeShort} / GMC Format</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748B" }}><X size={16} /></button>
        </div>

        {step === "details" && (
          <>
            <div style={{ marginBottom: 14, background: "rgba(14,165,233,.08)", border: "1px solid rgba(14,165,233,.2)", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#7dd3fc", lineHeight: 1.6 }}>
              This will generate a complete GMC-format submission document including a probity declaration, evidence index with filenames, AI-written domain narratives, and a gap action plan. Downloads directly as a PDF.
            </div>
            {[["Full name", "name", "Dr. Jane Smith"], ["GMC number", "gmc", "1234567"], ["Date prepared", "date", today()]].map(([label, key, ph]) => (
              <div key={key} style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: "#64748B", letterSpacing: ".08em", textTransform: "uppercase", display: "block", marginBottom: 5 }}>{label}</label>
                <input value={userDetails[key]} onChange={e => setUserDetails(p => ({ ...p, [key]: e.target.value }))} placeholder={ph}
                  style={{ width: "100%", background: "#1b3355", border: "1px solid #213045", borderRadius: 8, color: "#F2F4F7", fontFamily: "inherit", fontSize: 13, padding: "8px 11px" }} />
              </div>
            ))}
            {error && <div style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.2)", borderRadius: 8, padding: "8px 12px", color: "#fca5a5", fontSize: 12, marginBottom: 12 }}>{error}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
              <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #213045", background: "none", color: "#7a9ab8", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>Cancel</button>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 11.5, color: "#7a9ab8" }}>
                  <div onClick={() => setAiMode(m => !m)}
                    style={{ width: 32, height: 18, borderRadius: 9, background: aiMode ? sp.color : "#224068", position: "relative", transition: "background .2s", cursor: "pointer", flexShrink: 0 }}>
                    <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: aiMode ? 17 : 3, transition: "left .2s" }} />
                  </div>
                  AI narratives
                </label>
                <button onClick={generate} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: sp.color, color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
                  <Sparkles size={12} /> Generate Pack
                </button>
              </div>
            </div>
          </>
        )}

        {step === "generating" && (
          <div style={{ padding: "8px 0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <div style={{ width: 26, height: 26, border: `3px solid #213045`, borderTopColor: sp.color, borderRadius: "50%", animation: "spin 1s linear infinite", flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 13, color: "#9dbdd8", fontWeight: 500 }}>{aiMode ? "Writing AI narratives..." : "Compiling pack..."}</div>
                <div style={{ fontSize: 11, color: "#64748B", marginTop: 1 }}>{progress}</div>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {sp.domains.map((d, i) => {
                const done = domainsDone.includes(d.id);
                const active = progress.includes(`D${i+1}`);
                return (
                  <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `1.5px solid ${done ? "#10b981" : active ? sp.color : "#224068"}`, background: done ? "rgba(16,185,129,.12)" : active ? `${sp.color}15` : "#1b3355", transition: "all .3s" }}>
                      {done && <CheckCircle size={10} style={{ color: "#10b981" }} />}
                      {active && !done && <div style={{ width: 6, height: 6, borderRadius: "50%", background: sp.color }} />}
                    </div>
                    <span style={{ fontSize: 11.5, color: done ? "#10b981" : active ? "#e2e8f0" : "#4a6a8a", transition: "color .3s" }}>D{i+1} — {d.label}</span>
                    {done && narrSource[d.id] && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: narrSource[d.id] === "ai" ? "rgba(47,175,159,.12)" : "rgba(100,116,139,.12)", color: narrSource[d.id] === "ai" ? "#2FAF9F" : "#64748b" }}>{narrSource[d.id] === "ai" ? "AI" : "template"}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {step === "preview" && (
          <>
            <div style={{ background: "rgba(16,185,129,.08)", border: "1px solid rgba(16,185,129,.2)", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#86efac", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
              <CheckCircle size={14} style={{ color: "#10b981", flexShrink: 0 }} />
              Submission pack ready — {sp.domains.length} domain narratives generated, evidence index compiled, gap report included.
            </div>
            <div style={{ background: "#1b3355", borderRadius: 8, padding: "12px 14px", marginBottom: 16, maxHeight: 200, overflowY: "auto" }}>
              {sp.domains.map((d,i) => (
                <div key={d.id} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: ".07em" }}>D{i+1} {d.label}</span>
                    {narrSource[d.id] && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: narrSource[d.id] === "ai" ? "rgba(47,175,159,.12)" : "rgba(100,116,139,.1)", color: narrSource[d.id] === "ai" ? "#2FAF9F" : "#64748b" }}>{narrSource[d.id] === "ai" ? "✨ AI-written" : "template"}</span>}
                  </div>
                  <div style={{ fontSize: 11.5, color: "#8aabcc", lineHeight: 1.65 }}>{(narratives[d.id] || "").slice(0, 220)}{(narratives[d.id] || "").length > 220 ? "…" : ""}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button onClick={() => setStep("details")} style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #213045", background: "none", color: "#7a9ab8", cursor: "pointer", fontSize: 11.5, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}><RefreshCw size={11} />Regenerate</button>
              <button onClick={copyHTML} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #213045", background: "none", color: copied ? "#10b981" : "#8aabcc", cursor: "pointer", fontSize: 11.5, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4, transition: "color .2s" }}>
                {copied ? <><Check size={11} />Copied!</> : <><Copy size={11} />Copy HTML</>}
              </button>
              <button onClick={downloadPDF} disabled={pdfGenerating} style={{ padding: "7px 18px", borderRadius: 8, border: "none", background: pdfGenerating ? "#0d7a6e" : "#10b981", color: "#fff", cursor: pdfGenerating ? "default" : "pointer", fontSize: 11.5, fontWeight: 600, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5, opacity: pdfGenerating ? 0.8 : 1, transition: "background .2s" }}>
                {pdfGenerating
                  ? <><div style={{ width: 11, height: 11, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 1s linear infinite" }} />Generating…</>
                  : <><Download size={11} />Download PDF</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Anonymise Modal ──────────────────────────────────────────────────────────
function AnonymiseModal({ file, onClose }) {
  const isPDF = (file.name || "").toLowerCase().endsWith(".pdf");
  const [status, setStatus]               = useState("processing"); // processing | done | error
  const [redactedPdfB64, setRedactedPdfB64] = useState("");          // PDF mode
  const [anonymisedText, setAnonymisedText] = useState("");          // text mode
  const [redacted, setRedacted]           = useState([]);            // list of strings found+redacted
  const [replacements, setReplacements]   = useState([]);            // text mode replacement pairs
  const [errorMsg, setErrorMsg]           = useState("");
  const [showRedactions, setShowRedactions] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        if (isPDF) {
          // ── PDF mode: true position-aware redaction on the server ──────────
          const b64 = await toBase64(file);
          const res = await apiFetch(`${API}/api/redact-pdf`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ base64: b64 }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error?.message || "Redaction failed");
          if (cancelled) return;
          setRedactedPdfB64(data.base64);
          setRedacted(Array.isArray(data.redacted) ? data.redacted : []);
        } else {
          // ── Text mode: extract text → anonymise → show preview ────────────
          const ext = (file.name || "").split(".").pop().toLowerCase();
          let text = "";

          if (ext === "docx" || ext === "doc") {
            const mammoth = await import("mammoth");
            const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
            text = result.value;
          } else {
            text = await new Promise((res, rej) => {
              const r = new FileReader();
              r.onload = e => res(e.target.result);
              r.onerror = () => rej(new Error("Read failed"));
              r.readAsText(file);
            });
          }

          if (cancelled) return;
          if (!text || text.trim().length < 10) {
            throw new Error("Could not extract readable text from this file.");
          }

          const aRes = await apiFetch(`${API}/api/anonymise`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text }),
          });
          const aData = await aRes.json();
          if (!aRes.ok) throw new Error(aData.error?.message || "Anonymisation failed");
          if (cancelled) return;
          setAnonymisedText(aData.anonymisedText);
          setReplacements(aData.replacements || []);
        }

        if (!cancelled) setStatus("done");
      } catch (e) {
        if (!cancelled) { setErrorMsg(e.message); setStatus("error"); }
      }
    };

    run();
    return () => { cancelled = true; };
  }, [file, isPDF]);

  // PDF mode: direct binary download — no print dialog
  const downloadRedactedPDF = () => {
    const bytes = Uint8Array.from(atob(redactedPdfB64), c => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `redacted_${file.name}`;
    a.click(); URL.revokeObjectURL(url);
  };

  // Text mode: build a styled HTML document and open print-to-PDF
  const downloadTextPDF = () => {
    const baseName = file.name.replace(/\.[^.]+$/, "");
    const dateStr = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    const PLACEHOLDER_RE = /\[([A-Z_]+)\]/g;
    const bodyHtml = anonymisedText.split("\n").map(line => {
      if (!line.trim()) return "<br/>";
      const escaped = line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const redactedLine = escaped.replace(PLACEHOLDER_RE, (_m, label) => {
        const widths = { PATIENT_NAME: 110, NHS_NUMBER: 90, DATE_OF_BIRTH: 80, ADDRESS: 130, POSTCODE: 60, PHONE_NUMBER: 80, EMAIL: 110, HOSPITAL_NUMBER: 90, GP_NAME: 100, GP_PRACTICE: 120, THIRD_PARTY_NAME: 110 };
        return `<span class="redacted" style="width:${widths[label] || 90}px" title="${label}"></span>`;
      });
      return `<p>${redactedLine}</p>`;
    }).join("");
    const redactionList = replacements.length > 0
      ? `<ul>${replacements.map(r => `<li><strong>${r.placeholder}</strong> — originally: <em>${r.found}</em></li>`).join("")}</ul>`
      : "<p>No patient-identifiable information was detected.</p>";
    const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/><title>Anonymised — ${baseName}</title><style>@page{margin:2cm;size:A4}*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11pt;color:#111;line-height:1.6}header{border-bottom:2px solid #111;padding-bottom:10px;margin-bottom:20px}header h1{font-size:14pt;margin:0 0 4px}header p{font-size:9pt;color:#555;margin:0}.badge{display:inline-block;background:#111;color:#fff;font-size:8pt;font-weight:700;padding:2px 8px;border-radius:3px;margin-top:6px}.document-body p{margin:0 0 6px}.redacted{display:inline-block;background:#111;height:.9em;border-radius:2px;vertical-align:middle}.redaction-log{margin-top:30px;border-top:1px solid #ccc;padding-top:16px}.redaction-log h2{font-size:10pt;text-transform:uppercase;letter-spacing:.08em;color:#555}.redaction-log ul{font-size:9pt;color:#333;padding-left:18px}.redaction-log li{margin-bottom:3px}footer{margin-top:30px;border-top:1px solid #ccc;padding-top:8px;font-size:8pt;color:#888}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style></head><body><header><h1>Anonymised Document</h1><p>Original file: <strong>${baseName}</strong> · Anonymised: <strong>${dateStr}</strong></p><span class="badge">PATIENT DATA REDACTED</span></header><div class="document-body">${bodyHtml}</div><div class="redaction-log"><h2>Redaction log (${replacements.length} item${replacements.length !== 1 ? "s" : ""})</h2>${redactionList}</div><footer>Patient-identifiable information removed for CESR evidence submission.</footer></body></html>`;
    const win = window.open("", "_blank");
    if (!win) { alert("Please allow pop-ups to download the PDF."); return; }
    win.document.write(html); win.document.close();
    win.onload = () => { win.focus(); win.print(); };
  };

  const processingLabel = isPDF ? "Detecting and redacting patient identifiers…" : "Anonymising document…";
  const allRedacted = isPDF ? redacted : replacements.map(r => r.found);

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.8)", backdropFilter: "blur(6px)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      <div style={{ background: "#162e4a", border: "1px solid #213045", borderRadius: 16, width: "100%", maxWidth: 620, maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "20px 24px 16px" }}>
          <div>
            <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 17, fontWeight: 700, color: "#fff", display: "flex", alignItems: "center", gap: 8 }}>
              <Shield size={16} style={{ color: "#0ea5e9" }} />
              {isPDF ? "Redact PDF" : "Anonymise Document"}
            </div>
            <div style={{ fontSize: 11.5, color: "#64748B", marginTop: 3 }}>{file.name}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748B", padding: 2 }}><X size={16} /></button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "0 24px 20px" }}>

          {/* Processing */}
          {status === "processing" && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 0", gap: 12 }}>
              <Loader size={24} style={{ color: "#0ea5e9", animation: "spin 1s linear infinite" }} />
              <div style={{ fontSize: 13, color: "#7dd3fc" }}>{processingLabel}</div>
              {isPDF && <div style={{ fontSize: 11, color: "#64748B", textAlign: "center", maxWidth: 320 }}>The original PDF layout, tables, and formatting will be preserved — only the identified data will be blacked out.</div>}
            </div>
          )}

          {/* Error */}
          {status === "error" && (
            <div style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.2)", borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#fca5a5", marginBottom: 4 }}>Redaction failed</div>
              <div style={{ fontSize: 12, color: "#fca5a5", opacity: .85 }}>{errorMsg}</div>
            </div>
          )}

          {/* Done */}
          {status === "done" && (
            <>
              {/* Banner */}
              <div style={{ background: "rgba(16,185,129,.08)", border: "1px solid rgba(16,185,129,.2)", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#6ee7b7", lineHeight: 1.6, marginBottom: 14 }}>
                {isPDF
                  ? `${allRedacted.length} item${allRedacted.length !== 1 ? "s" : ""} redacted directly in the original PDF. The layout, tables, and formatting are preserved — download to confirm.`
                  : "Patient-identifiable information has been replaced with placeholders. Review below, then download a PDF with black redaction bars."}
              </div>

              {/* Redaction list */}
              {allRedacted.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <button
                    onClick={() => setShowRedactions(x => !x)}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 5, color: "#64748B", fontSize: 11.5, fontWeight: 600 }}
                  >
                    {showRedactions ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    {allRedacted.length} item{allRedacted.length !== 1 ? "s" : ""} redacted — click to review
                  </button>
                  {showRedactions && (
                    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                      {allRedacted.map((s, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5 }}>
                          <span style={{ color: "#ef4444", fontFamily: "monospace", background: "rgba(239,68,68,.1)", padding: "1px 6px", borderRadius: 4 }}>{s}</span>
                          <span style={{ color: "#64748B" }}>→</span>
                          <span style={{ display: "inline-block", background: "#111827", borderRadius: 2, width: 60, height: "0.8em", verticalAlign: "middle" }} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Text mode preview */}
              {!isPDF && (
                <>
                  <div style={{ marginBottom: 4, fontSize: 10, fontWeight: 700, color: "#64748B", letterSpacing: ".08em", textTransform: "uppercase" }}>Preview</div>
                  <div style={{ background: "#0f1e30", border: "1px solid #213045", borderRadius: 10, padding: "14px 16px", fontSize: 11.5, color: "#8aabcc", lineHeight: 1.9, overflowY: "auto", maxHeight: 260, whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "monospace" }}>
                    {anonymisedText.split(/(\[[A-Z_]+\])/).map((part, i) =>
                      /^\[[A-Z_]+\]$/.test(part)
                        ? <span key={i} title={part} style={{ display: "inline-block", background: "#111827", borderRadius: 2, width: 72, height: "0.85em", verticalAlign: "middle", margin: "0 2px" }} />
                        : part
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {status === "done" && (
          <div style={{ padding: "14px 24px", borderTop: "1px solid #213045", display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button onClick={onClose} style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid #213045", background: "none", color: "#64748B", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>
              Close
            </button>
            <button
              onClick={isPDF ? downloadRedactedPDF : downloadTextPDF}
              style={{ padding: "7px 16px", borderRadius: 8, border: "none", background: "#0ea5e9", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5 }}
            >
              <Download size={12} />
              {isPDF ? `Download Redacted PDF` : "Download PDF"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Evidence Item ────────────────────────────────────────────────────────────
function EvidenceItem({ item, ev, onUpdate, domainColor, domainId, specialtyId, themeMode, onCvFileData, deadline, onSetDeadline, onRemoveDeadline }) {
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded]   = useState(false);
  const [editNotes, setEditNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(ev.notes || "");
  const [dragOver, setDragOver] = useState(false);
  const [anonymiseFile, setAnonymiseFile] = useState(null);
  const [cachedFile, setCachedFile] = useState(null);
  const [showExpiryPicker, setShowExpiryPicker] = useState(false);
  const fileRef = useRef();
  const anonRef = useRef();
  const domNum = DOMAIN_NUM[domainId] || "D?";
  const isLight = themeMode === "clinical_light";
  const t = {
    cardBg:       isLight ? "#FFFFFF"  : "#162e4a",
    cardBorder:   isLight ? "#E2E8F0"  : "#224068",
    tagBg:        isLight ? "#DBEAFE"  : "#1b3355",
    tagText:      isLight ? "#1E40AF"  : "#4a6e99",
    labelText:    isLight ? "#1F2D3D"  : "#F2F4F7",
    bodyText:     isLight ? "#5F738A"  : "#64748B",
    dimText:      isLight ? "#94A3B8"  : "#7a9ab8",
    progressBg:   isLight ? "#E2E8F0"  : "#224068",
    analysisBg:   isLight ? "#F8FAFC"  : "#0F1F2E",
    analysisBrd:  isLight ? "#E2E8F0"  : "#172235",
    analysisText: isLight ? "#374151"  : "#7a9ab8",
    strengthText: isLight ? "#065F46"  : "#d1fae5",
    gapsText:     isLight ? "#991B1B"  : "#fecaca",
    recText:      isLight ? "#374151"  : "#cbd5e1",
    notesDivBrd:  isLight ? "#E2E8F0"  : "#101e32",
    notesBtnTxt:  isLight ? "#94A3B8"  : "#7a9ab8",
    notesBg:      isLight ? "#FFFFFF"  : "#1b3355",
    notesBorder:  isLight ? "#E2E8F0"  : "#213045",
    notesText:    isLight ? "#374151"  : "#cbd5e1",
    suggestText:  isLight ? "#0369a1"  : "#7dd3fc",
    acceptsText:  isLight ? "#94A3B8"  : "#7a9ab8",
    subBg:        isLight ? "#F1F5F9"  : "#1b3355",
    subBorder:    isLight ? "#E2E8F0"  : "#213045",
  };

  const analyse = async file => {
    setUploading(true);
    try {
      // For CV items, extract file content and cache it so CVAnalyser can use it
      // without asking the user to upload again
      if (item.docType === "CV" && onCvFileData) {
        const ext = file.name.split(".").pop().toLowerCase();
        try {
          if (ext === "pdf") {
            const b64 = await toBase64(file);
            onCvFileData({ type: "pdf", base64: b64, mediaType: "application/pdf", name: file.name });
          } else if (ext === "docx" || ext === "doc") {
            const mammoth = await import("mammoth");
            const res = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
            onCvFileData({ type: "text", text: res.value, name: file.name });
          } else {
            const text = await new Promise((res, rej) => {
              const r = new FileReader();
              r.onload = e => res(e.target.result);
              r.onerror = () => rej(new Error("Read failed"));
              r.readAsText(file);
            });
            onCvFileData({ type: "text", text, name: file.name });
          }
        } catch { /* non-fatal — CVAnalyser will prompt for re-upload if needed */ }
      }
      const analysed = await analyseEvidenceFile({ specialtyId, domainId, item, file });
      onUpdate(item.id, analysed);
      setCachedFile(file);
      setExpanded(true);
      // Auto-create deadline record from validity rule or AI expiry extraction
      if (onSetDeadline) {
        try {
          const today = new Date().toISOString().split("T")[0];
          const evidenceDate = analysed.analysis?.evidenceDate || null;

          if (analysed.matchedValidityRule && analysed.validityData) {
            // GMC validity rule match — most authoritative source
            const baseDate = evidenceDate || today;
            const expiryDate = addYearsToDate(baseDate, analysed.validityData.validityYears);
            const gmcCurrencyExpiry = evidenceDate ? expiryDate : null;
            await onSetDeadline(item.id, domainId, {
              expiryDate,
              expiryType: "certificate",
              expiryLabel: `${analysed.matchedValidityRule} expiry`,
              renewalLeadTime: analysed.validityData.renewalLeadWeeks || 6,
              renewalUrl: analysed.validityData.renewalUrl,
              source: "gmc_currency_rule",
              evidenceDate,
              evidenceDateConfidence: analysed.analysis?.evidenceDateConfidence,
              evidenceDateNote: analysed.analysis?.evidenceDateNote,
              evidenceValidityRule: analysed.matchedValidityRule,
              gmcCurrencyExpiry,
            });
          } else if (analysed.expiryData?.expiryDate) {
            // Claude-detected expiry — fallback when no validity rule matched
            await onSetDeadline(item.id, domainId, {
              ...analysed.expiryData,
              source: "ai_detected",
              evidenceDate,
              evidenceDateConfidence: analysed.analysis?.evidenceDateConfidence,
              evidenceDateNote: analysed.analysis?.evidenceDateNote,
            });
          }
        } catch { /* non-fatal */ }
      }
    } catch (e) { alert("Analysis failed: " + e.message); }
    finally { setUploading(false); }
  };

  const st = ev.status || "missing";
  const StIcon = st === "complete" ? CheckCircle : st === "partial" ? Clock : AlertCircle;
  const stColor = st === "complete" ? "#10b981" : st === "partial" ? "#f59e0b" : "#7a9ab8";
  const stLabel = st === "complete" ? "Complete" : st === "partial" ? "Partial" : "Upload Required";
  const onDropFile = e => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    const f = e.dataTransfer?.files?.[0];
    if (f) analyse(f);
  };

  return (
    <>
    {anonymiseFile && <AnonymiseModal file={anonymiseFile} onClose={() => setAnonymiseFile(null)} />}
    <div
      onDragOver={e => { e.preventDefault(); if (!uploading) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDropFile}
      style={{
        border: `1px solid ${dragOver ? domainColor + "75" : expanded ? domainColor + "45" : t.cardBorder}`,
        borderRadius: showExpiryPicker ? "12px 12px 0 0" : 12,
        overflow: "hidden",
        background: dragOver ? `${domainColor}10` : t.cardBg,
        transition: "border-color .2s, background .2s"
      }}
    >
      <div style={{ padding: "12px 14px", display: "flex", alignItems: "flex-start", gap: 10 }}>
        <StIcon size={15} style={{ color: stColor, flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: t.tagText, background: t.tagBg, padding: "1px 5px", borderRadius: 4, fontFamily: "monospace" }}>{domNum}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: t.labelText }}>{item.label}</span>
            {item.required && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: "rgba(239,68,68,.15)", color: "#fca5a5", letterSpacing: ".04em" }}>REQUIRED</span>}
          </div>
          <div style={{ fontSize: 11.5, color: t.bodyText, marginTop: 3, lineHeight: 1.5 }}>{item.description}</div>
          {ev.fileName && (
            <div style={{ marginTop: 5, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, color: t.bodyText, display: "flex", alignItems: "center", gap: 3 }}><FileText size={10} />{ev.fileName}</span>
              {ev.suggestedFilename && (
                <div style={{ display: "flex", alignItems: "center", gap: 4, background: "rgba(14,165,233,.08)", border: "1px solid rgba(14,165,233,.2)", borderRadius: 5, padding: "2px 7px" }}>
                  <span style={{ fontSize: 10, color: t.suggestText, fontFamily: "monospace" }}>→ {ev.suggestedFilename}</span>
                  <CopyBtn text={ev.suggestedFilename} small />
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {/* GMC evidence age currency badge */}
          {(() => {
            const badge = getEvidenceAgeBadge(ev.analysis?.evidenceDate);
            if (!badge) return null;
            return (
              <span
                title={badge.tooltip}
                style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 7px", borderRadius: 20, border: `1px solid ${badge.brd}`, background: badge.bg, color: badge.color, fontSize: 9.5, fontWeight: 700, whiteSpace: "nowrap", cursor: "default" }}
              >
                {badge.level === "outside" ? "⚠ " : ""}{badge.label}
              </span>
            );
          })()}
          {/* Expiry badge — shown when a deadline exists */}
          {deadline && (() => {
            const u = getUrgency(deadline.expiry_date);
            return (
              <button
                onClick={() => setShowExpiryPicker(x => !x)}
                title={`Expires ${formatExpiryDate(deadline.expiry_date)}`}
                style={{ display: "flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 20, border: `1px solid ${u?.color || "#7a9ab8"}40`, background: `${u?.color || "#7a9ab8"}12`, color: u?.color || "#7a9ab8", cursor: "pointer", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}
              >
                <Calendar size={9} />{u?.shortLabel || "—"}
              </button>
            );
          })()}
          <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: st === "complete" ? "rgba(16,185,129,.12)" : st === "partial" ? "rgba(245,158,11,.12)" : "rgba(71,85,105,.1)", color: stColor, border: `1px solid ${stColor}28`, whiteSpace: "nowrap" }}>{stLabel}</span>
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            style={{ padding: "5px 10px", borderRadius: 7, border: `1px solid ${domainColor}40`, background: `${domainColor}0e`, color: domainColor, cursor: uploading ? "not-allowed" : "pointer", fontSize: 11, fontWeight: 600, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4, opacity: uploading ? .6 : 1, whiteSpace: "nowrap" }}>
            {uploading ? <Loader size={10} style={{ animation: "spin 1s linear infinite" }} /> : <Upload size={10} />}
            {uploading ? "Analysing…" : ev.fileName ? "Re-upload" : "Upload"}
          </button>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx" style={{ display: "none" }} onChange={e => { const f = e.target.files[0]; if (f) analyse(f); e.target.value = ""; }} />
          <button
            onClick={() => cachedFile ? setAnonymiseFile(cachedFile) : anonRef.current?.click()}
            title={cachedFile || ev.fileName ? `Anonymise ${ev.fileName || "uploaded document"}` : "Anonymise a document to remove patient-identifiable information"}
            style={{ padding: "5px 10px", borderRadius: 7, border: "1px solid rgba(16,185,129,.3)", background: "rgba(16,185,129,.07)", color: "#10b981", cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
            <Shield size={10} />{cachedFile ? "Anonymise" : ev.fileName ? "Anonymise…" : "Anonymise"}
          </button>
          <input ref={anonRef} type="file" accept=".pdf,.doc,.docx,.txt,.rtf" style={{ display: "none" }} onChange={e => { const f = e.target.files[0]; if (f) setAnonymiseFile(f); e.target.value = ""; }} />
          {/* Set expiry date button */}
          <button
            onClick={() => setShowExpiryPicker(x => !x)}
            title="Set expiry date"
            style={{ padding: "5px 8px", borderRadius: 7, border: `1px solid ${showExpiryPicker ? "#2FAF9F60" : "#2FAF9F30"}`, background: showExpiryPicker ? "rgba(47,175,159,.14)" : "rgba(47,175,159,.06)", color: "#2FAF9F", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, fontFamily: "inherit", whiteSpace: "nowrap", transition: "all .12s" }}>
            <CalendarClock size={10} />{deadline ? "Edit expiry" : "Set expiry"}
          </button>
          {ev.analysis && <button onClick={() => setExpanded(x => !x)} style={{ background: "none", border: "none", cursor: "pointer", color: t.bodyText, padding: 3 }}>{expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}</button>}
        </div>
      </div>

      {/* AI-detected expiry notification */}
      {ev.expiryData?.expiryDate && deadline && (
        <div style={{ margin: "0 14px 8px 37px", display: "flex", alignItems: "center", gap: 6, padding: "5px 9px", background: "rgba(47,175,159,.08)", border: "1px solid rgba(47,175,159,.2)", borderRadius: 7 }}>
          <Calendar size={10} style={{ color: "#2FAF9F", flexShrink: 0 }} />
          <span style={{ fontSize: 10.5, color: "#2FAF9F", fontWeight: 600 }}>Expiry tracked: {formatExpiryDate(ev.expiryData.expiryDate)}</span>
          <span style={{ fontSize: 10, color: "#2FAF9F80" }}>· AI detected</span>
        </div>
      )}

      {/* Empty state — show prominent upload prompt when nothing uploaded yet */}
      {!ev.fileName && !ev.analysis && (
        <div onClick={() => fileRef.current?.click()}
          style={{ margin: "0 14px 10px 37px", border: `1.5px dashed ${dragOver ? domainColor + "80" : domainColor + "35"}`, borderRadius: 8, padding: "9px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: "all .15s", background: dragOver ? `${domainColor}12` : `${domainColor}05` }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = domainColor + "70"; e.currentTarget.style.background = domainColor + "0d"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = domainColor + "35"; e.currentTarget.style.background = domainColor + "05"; }}>
          <Upload size={13} style={{ color: domainColor, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11.5, color: domainColor, fontWeight: 600 }}>{dragOver ? "Drop file to upload" : "Upload evidence"}</div>
            <div style={{ fontSize: 10.5, color: t.acceptsText, marginTop: 1 }}>Accepts: {item.accepts}</div>
          </div>
        </div>
      )}
      {ev.fileName && (
        <div style={{ padding: "0 14px 7px 37px", fontSize: 10.5, color: t.acceptsText, display: "flex", alignItems: "center", gap: 4 }}>
          <Upload size={9} />Accepts: {item.accepts}
        </div>
      )}

      {ev.analysis && expanded && (
        <div style={{ borderTop: `1px solid ${t.analysisBrd}`, padding: "12px 14px", background: t.analysisBg }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 20, background: scoreBg(ev.analysis.verdict), color: scoreColor(ev.analysis.verdict), border: `1px solid ${scoreColor(ev.analysis.verdict)}28` }}>{scoreLabel(ev.analysis.verdict)}</span>
            <span style={{ fontSize: 11, color: t.bodyText }}>Confidence: {ev.analysis.confidence}%</span>
            <div style={{ flex: 1 }} />
            <button onClick={() => onUpdate(item.id, { analysis: null, fileName: "", suggestedFilename: "", status: "missing" })} style={{ background: "none", border: "none", cursor: "pointer", color: t.bodyText, fontSize: 11, display: "flex", alignItems: "center", gap: 4, fontFamily: "inherit" }}><Trash2 size={10} />Remove</button>
          </div>
          <p style={{ fontSize: 12.5, color: t.analysisText, lineHeight: 1.65, marginBottom: 10 }}>{ev.analysis.summary}</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7, marginBottom: 8 }}>
            {ev.analysis.strengths?.length > 0 && (
              <div style={{ background: "rgba(16,185,129,.07)", border: "1px solid rgba(16,185,129,.14)", borderRadius: 8, padding: "8px 10px" }}>
                <div style={{ fontSize: 9.5, fontWeight: 700, color: "#10b981", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 4 }}>Strengths</div>
                {ev.analysis.strengths.map((s, i) => <div key={i} style={{ fontSize: 11.5, color: t.strengthText, lineHeight: 1.5, marginBottom: 3, display: "flex", gap: 5 }}><span style={{ color: "#10b981", flexShrink: 0 }}>✓</span>{s}</div>)}
              </div>
            )}
            {ev.analysis.gaps?.length > 0 && (
              <div style={{ background: "rgba(239,68,68,.07)", border: "1px solid rgba(239,68,68,.14)", borderRadius: 8, padding: "8px 10px" }}>
                <div style={{ fontSize: 9.5, fontWeight: 700, color: "#ef4444", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 4 }}>Gaps</div>
                {ev.analysis.gaps.map((g, i) => <div key={i} style={{ fontSize: 11.5, color: t.gapsText, lineHeight: 1.5, marginBottom: 3, display: "flex", gap: 5 }}><span style={{ color: "#ef4444", flexShrink: 0 }}>✗</span>{g}</div>)}
              </div>
            )}
          </div>
          {ev.analysis.recommendation && (
            <div style={{ background: `${domainColor}0a`, border: `1px solid ${domainColor}20`, borderRadius: 8, padding: "8px 11px" }}>
              <div style={{ fontSize: 9.5, fontWeight: 700, color: domainColor, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 4 }}>Recommendation</div>
              <div style={{ fontSize: 11.5, color: t.recText, lineHeight: 1.6 }}>{ev.analysis.recommendation}</div>
            </div>
          )}
        </div>
      )}

      <div style={{ borderTop: `1px solid ${t.notesDivBrd}`, padding: "6px 14px 6px 37px" }}>
        {!editNotes
          ? <button onClick={() => { setEditNotes(true); setNotesDraft(ev.notes || ""); }} style={{ fontSize: 11, color: t.notesBtnTxt, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
              📝 {ev.notes ? ev.notes.slice(0, 70) + (ev.notes.length > 70 ? "…" : "") : "Add note…"}
            </button>
          : <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
              <textarea value={notesDraft} onChange={e => setNotesDraft(e.target.value)} rows={2} autoFocus
                style={{ flex: 1, background: t.notesBg, border: `1px solid ${t.notesBorder}`, borderRadius: 6, color: t.notesText, fontFamily: "inherit", fontSize: 11.5, padding: "5px 9px", resize: "none" }}
                placeholder="Where it's filed, what's outstanding…" />
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <button onClick={() => { onUpdate(item.id, { notes: notesDraft }); setEditNotes(false); }} style={{ padding: "4px 9px", borderRadius: 5, border: "none", background: "#3b82f6", color: "#fff", cursor: "pointer", fontSize: 11, fontFamily: "inherit" }}>Save</button>
                <button onClick={() => setEditNotes(false)} style={{ padding: "4px 9px", borderRadius: 5, border: `1px solid ${t.notesBorder}`, background: "none", color: t.analysisText, cursor: "pointer", fontSize: 11, fontFamily: "inherit" }}>✕</button>
              </div>
            </div>
        }
      </div>
    </div>
    {/* Expiry date picker — rendered outside card so overflow:hidden doesn't clip it */}
    {showExpiryPicker && onSetDeadline && (
      <ExpiryDatePicker
        item={item}
        deadline={deadline}
        onSave={async (data) => {
          await onSetDeadline(item.id, domainId, {
            expiryDate: data.expiryDate,
            reminderOption: data.reminderOption,
            notes: data.notes,
            source: "manual",
          });
          setShowExpiryPicker(false);
        }}
        onRemove={async () => {
          if (deadline && onRemoveDeadline) await onRemoveDeadline(deadline.id);
          setShowExpiryPicker(false);
        }}
        onClose={() => setShowExpiryPicker(false)}
        themeMode={themeMode}
      />
    )}
    </>
  );
}

// ─── CV Analyser ──────────────────────────────────────────────────────────────
export function CVAnalyser({ specialty, evidence, onUpdate, themeMode, cvFileData }) {
  // Compute IDs before state so lazy initialisers can seed from persisted evidence
  const sp = SPECIALTIES[specialty];
  const cvDomain = sp.domains.find(d => d.id === "cv");
  const cvItemId = cvDomain?.items[0]?.id;
  const savedCv  = cvItemId ? (evidence[cvItemId] || {}) : {};

  const [cvText, setCvText]     = useState(() => cvFileData?.type === "text" ? cvFileData.text : "");
  const [cvFile, setCvFile]     = useState(() => cvFileData?.type === "pdf" ? { base64: cvFileData.base64, mediaType: cvFileData.mediaType, name: cvFileData.name } : null);
  const [fileName, setFileName] = useState(() => cvFileData?.name || savedCv.fileName || "");
  const [loading, setLoading]   = useState(false);
  const [analysisMode, setAnalysisMode] = useState("fast"); // fast | deep
  const [result, setResult]     = useState(() => savedCv.cvAnalysis || null);
  const [error, setError]       = useState("");
  const [expanded, setExpanded] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [processing, setProcessing] = useState(false);
  const fileRef = useRef();

  // Sync whenever the parent caches a new CV file (e.g. uploaded via the evidence card above)
  useEffect(() => {
    if (!cvFileData) return;
    setError("");
    if (cvFileData.type === "pdf") {
      setCvFile({ base64: cvFileData.base64, mediaType: cvFileData.mediaType, name: cvFileData.name });
      setCvText("");
    } else {
      setCvText(cvFileData.text || "");
      setCvFile(null);
    }
    setFileName(cvFileData.name);
  }, [cvFileData]); // eslint-disable-line react-hooks/exhaustive-deps
  const isLight = themeMode === "clinical_light";
  const cv = {
    headingCol:   isLight ? "#1F2D3D"  : "#fff",
    bodyCol:      isLight ? "#5F738A"  : "#64748B",
    uploadBg:     isLight ? "#F8FAFC"  : "#162e4a",
    uploadBorder: isLight ? "#E2E8F0"  : "#224068",
    uploadIconBg: isLight ? "#F1F5F9"  : "#1b3355",
    uploadIconCol:isLight ? "#94A3B8"  : "#4a6580",
    labelText:    isLight ? "#1F2D3D"  : "#F2F4F7",
    textareaBg:   isLight ? "#FFFFFF"  : "#162e4a",
    textareaBrd:  isLight ? "#E2E8F0"  : "#213045",
    textareaDivBrd: isLight ? "#E2E8F0": "#172235",
    textareaText: isLight ? "#1F2D3D"  : "#F2F4F7",
    toggleBg:     isLight ? "#F1F5F9"  : "#162e4a",
    toggleBrd:    isLight ? "#E2E8F0"  : "#213045",
    inactiveBtnTxt: isLight ? "#64748B": "#7a9ab8",
    spinnerBrd:   isLight ? "#E2E8F0"  : "#213045",
    spinnerTxt:   isLight ? "#5F738A"  : "#7a9ab8",
    resultCardBg: isLight ? "#FFFFFF"  : "#162e4a",
    resultCardBrd:isLight ? "#E2E8F0"  : "#213045",
    ringTrack:    isLight ? "#E2E8F0"  : "#224068",
    ringText:     isLight ? "#1F2D3D"  : "#fff",
    summaryText:  isLight ? "#374151"  : "#7a9ab8",
    gridItemText: isLight ? "#374151"  : "#cbd5e1",
    noneText:     isLight ? "#94A3B8"  : "#7a9ab8",
    sectionBg:    isLight ? "#FFFFFF"  : "#162e4a",
    sectionBrd:   isLight ? "#E2E8F0"  : "#213045",
    sectionDivBrd:isLight ? "#F1F5F9"  : "#101e32",
    sectionHdrBrd:isLight ? "#F1F5F9"  : "#101e32",
    rowOpenBg:    isLight ? "#F1F5F9"  : "#1b3355",
    rowLabelText: isLight ? "#1F2D3D"  : "#F2F4F7",
    rowDimText:   isLight ? "#94A3B8"  : "#7a9ab8",
    findingBg:    isLight ? "#F8FAFC"  : "#1b3355",
    findingText:  isLight ? "#374151"  : "#8aabcc",
  };
  const gapRegex = /\b(gap|break|career break|unemploy|not employed|out of work|time off|absence)\b/i;

  const handleFile = async f => {
    if (!f) return;
    setError(""); setProcessing(true);
    const ext = f.name.split(".").pop().toLowerCase();
    setFileName(f.name);

    try {
      if (ext === "pdf") {
        // Send PDF directly to Claude as a document — no text extraction needed
        const b64 = await toBase64(f);
        setCvFile({ base64: b64, mediaType: "application/pdf", name: f.name });
        setCvText(""); // clear text area — PDF will be sent as document
      } else if (ext === "docx" || ext === "doc") {
        // Use mammoth to extract text from Word docs
        const mammoth = await import("mammoth");
        const arrayBuf = await f.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer: arrayBuf });
        const extracted = result.value;
        setCvText(extracted);
        setCvFile(null);
      } else {
        // Plain text / RTF — read as text
        const text = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = e => res(e.target.result);
          r.onerror = () => rej(new Error("Read failed"));
          r.readAsText(f);
        });
        setCvText(text);
        setCvFile(null);
      }
    } catch (e) {
      setError("Could not read file: " + e.message + ". Try copying and pasting your CV text instead.");
      setFileName("");
    } finally {
      setProcessing(false);
    }
  };

  const clearAll = () => {
    setCvText(""); setCvFile(null); setFileName(""); setResult(null); setError("");
    if (onUpdate && cvItemId) onUpdate(cvItemId, { status: "missing", fileName: "", analysis: null, cvAnalysis: null });
  };

  const charCount = cvText.length;
  const hasPDF = !!cvFile;

  const analyse = async () => {
    if (!hasPDF && cvText.trim().length < 300) {
      setError("Your CV text is too short for a meaningful analysis. Please upload your CV file or paste the full text.");
      return;
    }
    setLoading(true); setError(""); setResult(null);
    try {
      let userContent;
      if (hasPDF) {
        const extractRes = await apiFetch(`${API}/api/extract-pdf`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ base64: cvFile.base64 }),
        });
        const extractData = await extractRes.json();
        if (!extractRes.ok) throw new Error(extractData.error?.message || "PDF extraction failed");
        userContent = `Analyse this ${sp.name} doctor's CV against GMC CESR guidance:\n\n${extractData.text}`;
      } else {
        userContent = `Analyse this ${sp.name} doctor's CV against GMC CESR guidance:\n\n${cvText}`;
      }
      const res = await apiFetch(`${API}/api/openai/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system: `${sp.cvPrompt}\nKeep findings concise and practical.`,
          input: userContent,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || "API error");
      const parsed = parseAiJson(data.output_text || "");
      const detectedGapWarnings = (parsed.chronologyWarnings || []).filter(w => gapRegex.test(w || ""));
      const finalResult = { ...parsed, gapsInService: parsed.gapsInService || detectedGapWarnings };
      setResult(finalResult);
      if (onUpdate && cvItemId) onUpdate(cvItemId, { status: "complete", fileName: fileName, cvAnalysis: finalResult });
    } catch (e) { setError("Analysis failed: " + e.message); } finally { setLoading(false); }
  };

  const secCfg = { present: { color: "#10b981", bg: "rgba(16,185,129,.1)", border: "rgba(16,185,129,.2)", label: "Present", Icon: CheckCircle }, weak: { color: "#f59e0b", bg: "rgba(245,158,11,.1)", border: "rgba(245,158,11,.2)", label: "Weak", Icon: Clock }, missing: { color: "#ef4444", bg: "rgba(239,68,68,.1)", border: "rgba(239,68,68,.2)", label: "Missing", Icon: AlertCircle } };
  const ScoreRing = ({ score }) => { const c = cvScoreCol(score), r = 38, circ = 2 * Math.PI * r, dash = (score / 100) * circ; return (<svg width={90} height={90} viewBox="0 0 90 90" style={{ flexShrink: 0 }}><circle cx={45} cy={45} r={r} fill="none" stroke={cv.ringTrack} strokeWidth={7} /><circle cx={45} cy={45} r={r} fill="none" stroke={c} strokeWidth={7} strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" transform="rotate(-90 45 45)" style={{ transition: "stroke-dasharray .9s" }} /><text x={45} y={45} textAnchor="middle" dominantBaseline="central" style={{ fill: cv.ringText, fontSize: 18, fontWeight: 700, fontFamily: "'DM Serif Display',serif" }}>{score}</text></svg>); };

  return (
    <div style={{ maxWidth: 840 }}>
      {/* Sub-section header — sits below the DomainPanel evidence card */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 28, marginBottom: 14, paddingTop: 22, borderTop: `1px solid ${cv.textareaDivBrd}` }}>
        <Sparkles size={14} style={{ color: "#2FAF9F", flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: cv.headingCol }}>Full CV Analysis</span>
        <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 5px", borderRadius: 3, background: "rgba(47,175,159,.15)", color: "#2FAF9F" }}>AI</span>
        <span style={{ fontSize: 11.5, color: cv.bodyCol }}>— deep review against {sp.college} CESR guidance. Scores 13 sections, flags gaps in service, generates specific recommendations.</span>
      </div>

      {/* File zone — compact when loaded, full prompt when empty */}
      <div
        onClick={() => !processing && fileRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
        style={{ border: `2px dashed ${dragOver ? sp.color : hasPDF || fileName ? sp.color + "60" : cv.uploadBorder}`, borderRadius: 12, padding: "14px 16px", marginBottom: 12, cursor: processing ? "wait" : "pointer", background: hasPDF || fileName ? `${sp.color}08` : cv.uploadBg, transition: "all .2s", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: hasPDF || fileName ? `${sp.color}18` : cv.uploadIconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {processing ? <Loader size={16} style={{ color: sp.color, animation: "spin 1s linear infinite" }} /> : <Upload size={16} style={{ color: hasPDF || fileName ? sp.color : cv.uploadIconCol }} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {processing ? (
            <div style={{ fontSize: 13, color: sp.color, fontWeight: 600 }}>Reading file...</div>
          ) : hasPDF || fileName ? (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: cv.labelText, display: "flex", alignItems: "center", gap: 6 }}>
                <FileText size={13} style={{ color: sp.color }} />
                {fileName}
                {hasPDF && <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: `${sp.color}20`, color: sp.color }}>PDF — ready to analyse</span>}
                {!hasPDF && cvFileData && <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: `${sp.color}20`, color: sp.color }}>Loaded from above</span>}
              </div>
              <div style={{ fontSize: 11, color: cv.bodyCol, marginTop: 2 }}>Click to use a different file</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: cv.labelText }}>Upload your CV for deep analysis</div>
              <div style={{ fontSize: 11, color: cv.bodyCol, marginTop: 1 }}>PDF, Word (.docx), or plain text — or upload via the evidence card above and it loads here automatically</div>
            </>
          )}
        </div>
        {(hasPDF || fileName) && (
          <button onClick={e => { e.stopPropagation(); clearAll(); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: cv.bodyCol, padding: 4, flexShrink: 0 }}>
            <X size={14} />
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt,.rtf" style={{ display: "none" }} onChange={e => { handleFile(e.target.files[0]); e.target.value = ""; }} />

      {/* Text area — shown for non-PDF files, hidden when PDF loaded */}
      {!hasPDF && (
        <div style={{ background: cv.textareaBg, border: `1px solid ${cv.textareaBrd}`, borderRadius: 12, padding: "14px 16px", marginBottom: 11 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: cv.bodyCol, letterSpacing: ".1em", textTransform: "uppercase" }}>
              {cvText ? "Extracted / Pasted Text" : "Or paste CV text"}
            </span>
          </div>
          <textarea value={cvText} onChange={e => { setCvText(e.target.value); setError(""); }} rows={10}
            placeholder={`Paste your ${sp.name} CV here — qualifications, employment history, clinical experience, teaching, audit, research, management, CPD, referees…`}
            style={{ width: "100%", background: "transparent", border: "none", color: cv.textareaText, fontFamily: "inherit", fontSize: 12.5, lineHeight: 1.75, resize: "vertical", outline: "none", padding: 0 }} />
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${cv.textareaDivBrd}` }}>
            <span style={{ fontSize: 11, color: charCount < 300 ? "#ef4444" : charCount < 800 ? "#f59e0b" : charCount > 9000 ? "#f59e0b" : "#10b981" }}>{charCount.toLocaleString()} chars</span>
            {charCount > 0 && charCount < 300 && <span style={{ fontSize: 10, color: "#ef4444", fontWeight: 600 }}>Too short — paste full CV</span>}
            {charCount >= 300 && charCount < 800 && <span style={{ fontSize: 10, color: "#f59e0b", fontWeight: 600 }}>Add more detail</span>}
            {charCount >= 800 && charCount < 9000 && <span style={{ fontSize: 10, color: "#10b981", fontWeight: 600 }}>Good length</span>}
            {charCount >= 9000 && <span style={{ fontSize: 10, color: "#f59e0b", fontWeight: 600 }}>Very long — full text will be analysed</span>}
          </div>
        </div>
      )}

      {/* Analyse button row */}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginBottom: 11 }}>
        <div style={{ marginRight: "auto", display: "flex", gap: 6, background: cv.toggleBg, border: `1px solid ${cv.toggleBrd}`, borderRadius: 10, padding: 4 }}>
          <button
            onClick={() => setAnalysisMode("fast")}
            style={{
              padding: "6px 10px",
              borderRadius: 7,
              border: "none",
              background: analysisMode === "fast" ? "#1d4ed8" : "transparent",
              color: analysisMode === "fast" ? "#fff" : cv.inactiveBtnTxt,
              cursor: "pointer",
              fontSize: 11,
              fontWeight: 600,
              fontFamily: "inherit"
            }}
          >
            Fast
          </button>
          <button
            onClick={() => setAnalysisMode("deep")}
            style={{
              padding: "6px 10px",
              borderRadius: 7,
              border: "none",
              background: analysisMode === "deep" ? "#7c3aed" : "transparent",
              color: analysisMode === "deep" ? "#fff" : cv.inactiveBtnTxt,
              cursor: "pointer",
              fontSize: 11,
              fontWeight: 600,
              fontFamily: "inherit"
            }}
          >
            Deep
          </button>
        </div>
        {(cvText || hasPDF) && !loading && (
          <button onClick={clearAll} style={{ padding: "7px 12px", borderRadius: 8, border: `1px solid ${cv.toggleBrd}`, background: "transparent", color: cv.bodyCol, cursor: "pointer", fontSize: 12, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}><RefreshCw size={11} />Clear</button>
        )}
        <button onClick={analyse} disabled={loading || processing || (!hasPDF && !cvText)}
          style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: sp.color, color: "#fff", cursor: (loading || processing || (!hasPDF && !cvText)) ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6, opacity: (loading || processing || (!hasPDF && !cvText)) ? .5 : 1 }}>
          {loading ? <Loader size={12} style={{ animation: "spin 1s linear infinite" }} /> : <Sparkles size={12} />}
          {loading ? "Analysing..." : "Analyse CV"}
        </button>
      </div>
      {error && <div style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.2)", borderRadius: 9, padding: "10px 14px", color: "#fca5a5", fontSize: 12.5, marginBottom: 11 }}>{error}</div>}
      {loading && <div style={{ textAlign: "center", padding: "44px 20px" }}><div style={{ width: 34, height: 34, border: `3px solid ${cv.spinnerBrd}`, borderTopColor: sp.color, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 11px" }} /><div style={{ fontSize: 13, color: cv.spinnerTxt }}>Reviewing against {sp.collegeShort} / GMC CESR guidance…</div></div>}
      {result && !loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
          <div style={{ background: cv.resultCardBg, border: `1px solid ${cv.resultCardBrd}`, borderRadius: 13, padding: 20, display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
            <ScoreRing score={result.overallScore} />
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}><span style={{ fontFamily: "'DM Serif Display',serif", fontSize: 18, fontWeight: 700, color: cv.headingCol }}>{result.overallRating}</span><span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: `${cvScoreCol(result.overallScore)}15`, color: cvScoreCol(result.overallScore), border: `1px solid ${cvScoreCol(result.overallScore)}30` }}>{result.overallScore}/100</span></div>
              <p style={{ fontSize: 12.5, color: cv.summaryText, lineHeight: 1.65 }}>{result.summary}</p>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
            {[{ label: "Strengths", items: result.strengths, color: "#10b981", bg: "rgba(16,185,129,.07)", border: "rgba(16,185,129,.17)" }, { label: "Critical Gaps", items: result.criticalGaps, color: "#ef4444", bg: "rgba(239,68,68,.07)", border: "rgba(239,68,68,.17)" }, { label: "Gaps in Service", items: result.gapsInService, color: "#fb7185", bg: "rgba(244,63,94,.08)", border: "rgba(244,63,94,.2)" }, { label: "Warnings", items: [...(result.formatIssues||[]),...(result.chronologyWarnings||[])], color: "#f59e0b", bg: "rgba(245,158,11,.07)", border: "rgba(245,158,11,.17)" }].map(col => (
              <div key={col.label} style={{ background: col.bg, border: `1px solid ${col.border}`, borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 9.5, fontWeight: 700, color: col.color, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 6 }}>{col.label}</div>
                {col.items?.length ? col.items.map((s, i) => <div key={i} style={{ fontSize: 11.5, color: cv.gridItemText, marginBottom: 4, lineHeight: 1.45, display: "flex", gap: 5 }}><span style={{ color: col.color, flexShrink: 0 }}>•</span>{s}</div>) : <div style={{ fontSize: 11.5, color: cv.noneText }}>None identified</div>}
              </div>
            ))}
          </div>
          <div style={{ background: cv.sectionBg, border: `1px solid ${cv.sectionBrd}`, borderRadius: 12, overflow: "hidden" }}>
            <div style={{ padding: "11px 16px", borderBottom: `1px solid ${cv.sectionHdrBrd}` }}><div style={{ fontSize: 12.5, fontWeight: 700, color: cv.rowLabelText }}>Section-by-Section Review</div><div style={{ fontSize: 11, color: cv.rowDimText, marginTop: 1 }}>Click any row to expand</div></div>
            {result.sections?.map((sec, i) => { const cfg = secCfg[sec.status] || secCfg.missing; const { Icon } = cfg; const open = expanded === sec.id; return (
              <div key={sec.id} style={{ borderBottom: i < result.sections.length - 1 ? `1px solid ${cv.sectionDivBrd}` : "none" }}>
                <div onClick={() => setExpanded(open ? null : sec.id)} style={{ padding: "11px 16px", display: "flex", alignItems: "center", gap: 9, cursor: "pointer", background: open ? cv.rowOpenBg : "transparent", transition: "background .13s" }}>
                  <Icon size={13} style={{ color: cfg.color, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 12.5, fontWeight: 600, color: cv.rowLabelText }}>{sec.name}</div>{!open && <div style={{ fontSize: 11, color: cv.bodyCol, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 1 }}>{sec.finding}</div>}</div>
                  <span style={{ fontSize: 9.5, fontWeight: 700, padding: "2px 7px", borderRadius: 20, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, flexShrink: 0 }}>{cfg.label}</span>
                  {open ? <ChevronUp size={11} style={{ color: cv.bodyCol }} /> : <ChevronDown size={11} style={{ color: cv.bodyCol }} />}
                </div>
                {open && (<div style={{ padding: "2px 16px 13px 38px", display: "flex", flexDirection: "column", gap: 7 }}>
                  <div style={{ background: cv.findingBg, borderRadius: 7, padding: "8px 11px" }}><div style={{ fontSize: 9.5, fontWeight: 700, color: cv.bodyCol, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 4 }}>Finding</div><div style={{ fontSize: 12.5, color: cv.findingText, lineHeight: 1.65 }}>{sec.finding}</div></div>
                  <div style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 7, padding: "8px 11px" }}><div style={{ fontSize: 9.5, fontWeight: 700, color: cfg.color, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 4 }}>Recommendation</div><div style={{ fontSize: 12.5, color: cv.gridItemText, lineHeight: 1.65 }}>{sec.recommendation}</div></div>
                </div>)}
              </div>);
            })}
          </div>
          <button onClick={analyse} disabled={!hasPDF && cvText.trim().length < 300}
            title={!hasPDF && cvText.trim().length < 300 ? "Upload or paste your CV above to re-analyse" : ""}
            style={{ alignSelf: "flex-start", padding: "5px 13px", borderRadius: 7, border: `1px solid ${cv.toggleBrd}`, background: "none", color: cv.bodyCol, cursor: (!hasPDF && cvText.trim().length < 300) ? "not-allowed" : "pointer", fontSize: 12, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5, opacity: (!hasPDF && cvText.trim().length < 300) ? 0.45 : 1 }}><RefreshCw size={11} />Re-analyse</button>
        </div>
      )}
    </div>
  );
}

// ─── Domain Panel ─────────────────────────────────────────────────────────────
function DomainPanel({ domain, evidence, onUpdate, specialtyId, themeMode, onCvFileData, onBulkUpload, deadlines, onSetDeadline, onRemoveDeadline }) {
  const comp = domain.items.filter(i => evidence[i.id]?.status === "complete").length;
  const part = domain.items.filter(i => evidence[i.id]?.status === "partial").length;
  const miss = domain.items.filter(i => evidence[i.id]?.status === "missing").length;
  const pct  = Math.round((comp / domain.items.length) * 100);
  const Icon = domain.icon;
  const isLight = themeMode === "clinical_light";
  const headingCol = isLight ? "#1F2D3D" : "#fff";
  const bodyCol    = isLight ? "#5F738A" : "#64748B";
  const progTrack  = isLight ? "#E2E8F0" : "#224068";
  return (
    <div style={{ maxWidth: 840 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 5 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: `${domain.color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon size={19} style={{ color: domain.color }} /></div>
        <div style={{ flex: 1 }}><h1 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 20, fontWeight: 700, color: headingCol, letterSpacing: "-0.02em" }}>{DOMAIN_NUM[domain.id]} — {domain.label}</h1><p style={{ color: bodyCol, fontSize: 12, marginTop: 2 }}>{domain.description}</p></div>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 15, flexWrap: "wrap", alignItems: "center" }}>
        {[{ label: "Complete", val: comp, color: "#10b981", bg: "rgba(16,185,129,.1)" }, { label: "Partial", val: part, color: "#f59e0b", bg: "rgba(245,158,11,.1)" }, { label: "Missing", val: miss, color: "#7a9ab8", bg: "rgba(71,85,105,.1)" }].map(s => (
          <div key={s.label} style={{ padding: "4px 12px", borderRadius: 7, background: s.bg, border: `1px solid ${s.color}22` }}><span style={{ fontSize: 14, fontWeight: 700, color: s.color, fontFamily: "'DM Serif Display',serif" }}>{s.val}</span><span style={{ fontSize: 11, color: bodyCol, marginLeft: 4 }}>{s.label}</span></div>
        ))}
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 7, minWidth: 150 }}>
          <div style={{ flex: 1, height: 4, background: progTrack, borderRadius: 2, overflow: "hidden" }}><div style={{ width: `${pct}%`, height: "100%", background: domain.color, borderRadius: 2, transition: "width .6s" }} /></div>
          <span style={{ fontSize: 11, color: bodyCol }}>{pct}%</span>
        </div>
      </div>
      {onBulkUpload && (
        <BulkUploadPanel
          specialty={specialtyId}
          evidence={evidence}
          onBulkUpload={onBulkUpload}
          themeMode={themeMode}
          domainId={domain.id}
        />
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {domain.items.map(item => (
          <EvidenceItem
            key={item.id}
            item={item}
            ev={evidence[item.id] || {}}
            domainColor={domain.color}
            domainId={domain.id}
            onUpdate={onUpdate}
            specialtyId={specialtyId}
            themeMode={themeMode}
            onCvFileData={item.docType === "CV" ? onCvFileData : undefined}
            deadline={deadlines?.find(d => d.item_id === item.id && d.status === "active") || null}
            onSetDeadline={onSetDeadline}
            onRemoveDeadline={onRemoveDeadline}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Bulk Review Modal ────────────────────────────────────────────────────────
function BulkReviewModal({ specialty, evidence, queue, onConfirm, onClose, domainId }) {
  const sp = SPECIALTIES[specialty];
  const allCandidates = buildItemCandidates(specialty);
  // When opened from a domain panel, scope the dropdown to that domain only
  const grouped = SPECIALTIES[specialty].domains
    .filter(d => !domainId || d.id === domainId)
    .map(d => ({
      domain: d,
      items: allCandidates.filter(c => c.domainId === d.id),
    }));

  // editable list: [{file, itemId, isInvalid}]
  const [assignments, setAssignments] = useState(() =>
    queue.map(q => ({ file: q.file, itemId: q.item?.id ?? "", isInvalid: q.isInvalid }))
  );

  const getDuplicateWarning = (itemId, idx) => {
    if (!itemId) return null;
    const batchDupe = assignments.some((a, i) => i !== idx && a.itemId === itemId);
    if (batchDupe) return "Another file in this batch is assigned to the same section — one will overwrite the other.";
    const existingFile = evidence[itemId]?.fileName;
    if (existingFile) return `Replaces existing file: "${existingFile}"`;
    return null;
  };

  const setItemId = (idx, id) => setAssignments(prev => prev.map((a, i) => i === idx ? { ...a, itemId: id } : a));
  const remove   = idx => setAssignments(prev => prev.filter((_, i) => i !== idx));

  const ready      = assignments.filter(a => a.itemId && !a.isInvalid);
  const unassigned = assignments.filter(a => !a.itemId && !a.isInvalid);
  const invalid    = assignments.filter(a => a.isInvalid);

  const handleConfirm = () => {
    const valid = ready
      .map(a => ({ file: a.file, item: allCandidates.find(c => c.id === a.itemId) }))
      .filter(a => a.item);
    onConfirm(valid);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.75)", backdropFilter: "blur(4px)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#0F1F2E", border: "1px solid #224068", borderRadius: 16, width: "100%", maxWidth: 640, maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 80px rgba(0,0,0,.6)" }}>

        {/* Header */}
        <div style={{ padding: "16px 20px 12px", borderBottom: "1px solid #172235", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <Upload size={15} style={{ color: sp.color }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#F2F4F7", fontFamily: "'DM Serif Display',serif" }}>Review Document Assignments</div>
            <div style={{ fontSize: 11, color: "#64748B", marginTop: 1 }}>
              <span style={{ color: "#10b981", fontWeight: 600 }}>{ready.length} ready</span>
              {unassigned.length > 0 && <> · <span style={{ color: "#f59e0b", fontWeight: 600 }}>{unassigned.length} unassigned</span></>}
              {invalid.length   > 0 && <> · <span style={{ color: "#ef4444", fontWeight: 600 }}>{invalid.length} unsupported</span></>}
              {assignments.some((a, i) => getDuplicateWarning(a.itemId, i)) && <> · <span style={{ color: "#f59e0b", fontWeight: 600 }}>some duplicates flagged</span></>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748B", padding: 4, display: "flex", alignItems: "center", borderRadius: 6 }}
            onMouseEnter={e => e.currentTarget.style.color = "#F2F4F7"} onMouseLeave={e => e.currentTarget.style.color = "#64748B"}>
            <X size={15} />
          </button>
        </div>

        {/* Scrollable list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
          {assignments.length === 0 && (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#64748B", fontSize: 13 }}>No files remaining.</div>
          )}
          {assignments.map((a, idx) => {
            const warn = getDuplicateWarning(a.itemId, idx);
            const matchedItem = allCandidates.find(c => c.id === a.itemId);
            const matchedDomain = matchedItem ? SPECIALTIES[specialty].domains.find(d => d.id === matchedItem.domainId) : null;

            let rowState = a.isInvalid ? "invalid" : !a.itemId ? "unassigned" : warn ? "warn" : "ok";
            const stateColor = rowState === "ok" ? "#10b981" : rowState === "warn" ? "#f59e0b" : "#ef4444";
            const RowIcon = rowState === "ok" ? CheckCircle : rowState === "warn" ? AlertCircle : AlertCircle;

            return (
              <div key={idx} style={{ border: `1px solid ${rowState === "ok" ? "#1d3a50" : stateColor + "40"}`, borderRadius: 10, padding: "10px 12px", background: rowState === "ok" ? "#162e4a" : `${stateColor}08` }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <RowIcon size={14} style={{ color: stateColor, flexShrink: 0, marginTop: 2 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: "#F2F4F7", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 280 }}>
                        {a.file.name}
                      </span>
                      <span style={{ fontSize: 10, color: "#7a9ab8" }}>{a.file.size ? `${(a.file.size / 1024).toFixed(0)} KB` : ""}</span>
                    </div>

                    {a.isInvalid ? (
                      <div style={{ fontSize: 11, color: "#ef4444", marginTop: 4 }}>Unsupported file type — PDF, image, Word, Excel, or PowerPoint only.</div>
                    ) : (
                      <>
                        {matchedItem && (
                          <div style={{ fontSize: 10.5, color: "#64748B", marginTop: 2 }}>
                            Auto-matched: <span style={{ color: matchedDomain?.color || "#7a9ab8", fontWeight: 600 }}>{DOMAIN_NUM[matchedItem.domainId]}</span> — {matchedItem.label}
                          </div>
                        )}
                        <select
                          value={a.itemId}
                          onChange={e => setItemId(idx, e.target.value)}
                          style={{ marginTop: 6, width: "100%", background: "#1b3355", border: `1px solid ${a.itemId ? "#213045" : "#f59e0b60"}`, borderRadius: 6, color: a.itemId ? "#F2F4F7" : "#f59e0b", fontFamily: "inherit", fontSize: 11.5, padding: "5px 8px", cursor: "pointer" }}
                        >
                          <option value="">— Assign to a section —</option>
                          {grouped.map(g => (
                            <optgroup key={g.domain.id} label={`${DOMAIN_NUM[g.domain.id]} — ${g.domain.label}`}>
                              {g.items.map(item => (
                                <option key={item.id} value={item.id}>{item.label}</option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        {warn && (
                          <div style={{ marginTop: 5, fontSize: 10.5, color: "#f59e0b", display: "flex", alignItems: "flex-start", gap: 4, lineHeight: 1.4 }}>
                            <AlertCircle size={10} style={{ flexShrink: 0, marginTop: 1 }} /> {warn}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <button onClick={() => remove(idx)} title="Remove from queue"
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#7a9ab8", padding: 4, flexShrink: 0, display: "flex", alignItems: "center", borderRadius: 5, transition: "color .15s" }}
                    onMouseEnter={e => e.currentTarget.style.color = "#ef4444"} onMouseLeave={e => e.currentTarget.style.color = "#7a9ab8"}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 16px", borderTop: "1px solid #172235", display: "flex", gap: 8, justifyContent: "flex-end", flexShrink: 0, background: "#0c1828", borderRadius: "0 0 16px 16px" }}>
          {unassigned.length > 0 && (
            <span style={{ fontSize: 11, color: "#f59e0b", alignSelf: "center", marginRight: "auto" }}>
              {unassigned.length} file{unassigned.length !== 1 ? "s" : ""} will be skipped (unassigned)
            </span>
          )}
          <button onClick={onClose}
            style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #213045", background: "none", color: "#7a9ab8", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>
            Cancel
          </button>
          <button onClick={handleConfirm} disabled={!ready.length}
            style={{ padding: "7px 18px", borderRadius: 8, border: "none", background: ready.length ? sp.color : "#1b3355", color: ready.length ? "#fff" : "#4a6a8a", cursor: ready.length ? "pointer" : "not-allowed", fontSize: 12, fontWeight: 700, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6, transition: "background .15s" }}>
            <Sparkles size={12} /> Confirm & Analyse ({ready.length} file{ready.length !== 1 ? "s" : ""})
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Bulk Upload Panel ────────────────────────────────────────────────────────
function BulkUploadPanel({ specialty, evidence, onBulkUpload, themeMode, domainId }) {
  const sp = SPECIALTIES[specialty];
  const isLight = themeMode === "clinical_light";
  const boxBg     = isLight ? "#FFFFFF"  : "#162e4a";
  const boxBorder = isLight ? "#E2E8F0"  : "#213045";
  const titleCol  = isLight ? "#1F2D3D"  : "#F2F4F7";
  const textCol   = isLight ? "#5F738A"  : "#64748B";

  const [dragOver,   setDragOver]   = useState(false);
  const [queue,      setQueue]      = useState([]);
  const [showModal,  setShowModal]  = useState(false);
  const [analysing,  setAnalysing]  = useState(false);
  const [progress,   setProgress]   = useState({ current: 0, total: 0 });
  const [results,    setResults]    = useState(null);
  const fileRef = useRef();

  const prepareQueue = files => {
    // When scoped to a domain, only match against that domain's items
    const candidates = domainId
      ? buildItemCandidates(specialty).filter(c => c.domainId === domainId)
      : buildItemCandidates(specialty);
    const batchIds = new Set();
    return Array.from(files).map(file => {
      const mt = mediaTy(file);
      if (!mt) return { file, item: null, isInvalid: true };
      const item = findTopMatch(candidates, file.name);
      // Flag if the destination already has a file, or same section already claimed in this batch
      const isDuplicate = item ? (!!evidence[item.id]?.fileName || batchIds.has(item.id)) : false;
      if (item) batchIds.add(item.id);
      return { file, item, isDuplicate, isInvalid: false };
    });
  };

  const handleFiles = files => {
    if (!files?.length || analysing) return;
    setQueue(prepareQueue(files));
    setShowModal(true);
    setResults(null);
  };

  const handleConfirm = async assignments => {
    setShowModal(false);
    setAnalysing(true);
    setProgress({ current: 0, total: assignments.length });
    try {
      const res = await onBulkUpload(assignments, done => setProgress({ current: done, total: assignments.length }));
      setResults(res);
    } catch (e) {
      alert("Analysis failed: " + e.message);
    } finally {
      setAnalysing(false);
    }
  };

  return (
    <>
      {showModal && (
        <BulkReviewModal
          specialty={specialty}
          evidence={evidence}
          queue={queue}
          onConfirm={handleConfirm}
          onClose={() => setShowModal(false)}
          domainId={domainId}
        />
      )}
      <div
        onDragOver={e => { e.preventDefault(); if (!analysing) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer?.files); }}
        style={{ background: boxBg, border: `1px dashed ${dragOver ? sp.color + "80" : analysing ? sp.color + "40" : boxBorder}`, borderRadius: 12, padding: "14px 16px", marginBottom: 16, transition: "all .15s" }}
      >
        <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: titleCol, display: "flex", alignItems: "center", gap: 6 }}>
              <Upload size={13} style={{ color: sp.color }} /> Upload all documents here
            </div>
            <div style={{ fontSize: 11, color: textCol, marginTop: 2, lineHeight: 1.5 }}>
              {analysing
                ? `Analysing file ${progress.current} of ${progress.total}…`
                : "Drop multiple files and we'll auto-match each one, then let you review before analysis runs."}
            </div>
          </div>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={analysing}
            style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${sp.color}45`, background: `${sp.color}12`, color: sp.color, cursor: analysing ? "not-allowed" : "pointer", fontSize: 11.5, fontWeight: 700, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6, opacity: analysing ? .65 : 1 }}
          >
            {analysing ? <Loader size={12} style={{ animation: "spin 1s linear infinite" }} /> : <Upload size={12} />}
            {analysing ? `${progress.current}/${progress.total}` : "Choose files"}
          </button>
        </div>

        {analysing && (
          <div style={{ marginTop: 10, height: 3, background: "#1b3355", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: "100%", background: `linear-gradient(90deg, ${sp.color}, #2FAF9F)`, borderRadius: 2, width: `${progress.total ? Math.round((progress.current / progress.total) * 100) : 0}%`, transition: "width .4s" }} />
          </div>
        )}

        {results && !analysing && (
          <div style={{ marginTop: 10 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: results.failed.length ? 6 : 0 }}>
              {results.analysed > 0 && (
                <span style={{ fontSize: 11, color: "#10b981", fontWeight: 600 }}>✓ {results.analysed} filed &amp; analysed</span>
              )}
              {results.failed.length > 0 && (
                <span style={{ fontSize: 11, color: "#f59e0b", fontWeight: 600 }}>⚠ {results.failed.length} failed</span>
              )}
              <button onClick={() => { setResults(null); fileRef.current?.click(); }}
                style={{ fontSize: 11, color: sp.color, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
                Upload more
              </button>
            </div>
            {results.failed.length > 0 && (
              <div style={{ background: "rgba(239,68,68,.07)", border: "1px solid rgba(239,68,68,.18)", borderRadius: 7, padding: "7px 10px" }}>
                {results.failed.map(name => (
                  <div key={name} style={{ fontSize: 11, color: "#fca5a5", lineHeight: 1.6 }}>
                    <strong>{name}</strong>
                    {results.errors?.[name] && <span style={{ color: "#94a3b8", marginLeft: 6 }}>{results.errors[name]}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!analysing && !results && (
          <div style={{ marginTop: 8, fontSize: 10, color: isLight ? "#6C7A86" : "#7a9ab8" }}>
            You'll review and confirm section assignments before any AI analysis runs.
          </div>
        )}

        <input ref={fileRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
          style={{ display: "none" }} onChange={e => { handleFiles(e.target.files); e.target.value = ""; }} />
      </div>
    </>
  );
}

// ─── Overview (merged Dashboard + Checklist) ──────────────────────────────────
function Dashboard({ evidence, specialty, onNav, onSwitch, onPack, onBulkUpload, themeMode, deadlines = [], onNavToDeadlines }) {
  const sp = SPECIALTIES[specialty];
  const isLight = themeMode === "clinical_light";
  const boxBg = isLight ? "#FFFFFF" : "#162e4a";
  const boxBorder = isLight ? "#E2E8F0" : "#213045";
  const headingCol = isLight ? "#1F2D3D" : "#fff";
  const bodyCol = isLight ? "#5F738A" : "#64748B";
  const progressTrack = isLight ? "#E6EEF7" : "#1b3355";

  const allItems = sp.domains.flatMap(d =>
    d.items.map(i => ({ ...i, domain: d, status: evidence[i.id]?.status || "missing", ev: evidence[i.id] || {} }))
  );
  const complete = allItems.filter(i => i.status === "complete").length;
  const partial  = allItems.filter(i => i.status === "partial").length;
  const missing  = allItems.filter(i => i.status === "missing").length;
  const reqMiss  = allItems.filter(i => i.required && i.status === "missing").length;
  const pct      = Math.round((complete / allItems.length) * 100);

  const [filter, setFilter] = useState("all");
  const [reqOnly, setReqOnly] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = allItems.filter(i => {
    if (reqOnly && !i.required) return false;
    if (filter !== "all" && i.status !== filter) return false;
    if (search && !i.label.toLowerCase().includes(search.toLowerCase()) && !i.domain.label.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Group filtered items by domain for display
  const groupedByDomain = sp.domains.map(d => ({
    domain: d,
    items: filtered.filter(i => i.domain.id === d.id),
  })).filter(g => g.items.length > 0);

  const filterBtns = [
    { key: "all",      label: "All",      count: allItems.length,   color: "#7a9ab8" },
    { key: "missing",  label: "Missing",  count: missing,           color: "#ef4444" },
    { key: "partial",  label: "Partial",  count: partial,           color: "#f59e0b" },
    { key: "complete", label: "Complete", count: complete,          color: "#10b981" },
  ];

  // Build deadline urgency lookup keyed by item_id for inline pills
  const deadlineByItemId = {};
  (deadlines || []).forEach(d => {
    if (d.status === "active") deadlineByItemId[d.item_id] = d;
  });

  // Banner: deadlines expiring or expired (within 12 weeks)
  const expiredDl  = (deadlines || []).filter(d => { const u = getUrgency(d.expiry_date); return d.status === "active" && u?.level === "expired"; });
  const urgentDl   = (deadlines || []).filter(d => { const u = getUrgency(d.expiry_date); return d.status === "active" && u && u.level !== "expired" && u.daysUntil <= 84; });
  const showBanner = expiredDl.length > 0 || urgentDl.length > 0;

  return (
    <div style={{ maxWidth: 860 }}>
      {/* Page header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: isLight ? "#1d3a60" : "#7a9ab8", letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 4 }}>{sp.college}</div>
          <h1 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 22, fontWeight: 700, color: headingCol, letterSpacing: "-0.02em", lineHeight: 1.2, display: "flex", alignItems: "center", gap: 8 }}>
            {renderSpecialtyIcon(sp, 20)} {sp.name} — Portfolio
          </h1>
        </div>
        <button onClick={onPack} style={{ padding: "9px 16px", borderRadius: 10, border: `1px solid ${sp.color}45`, background: `${sp.color}12`, color: sp.color, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 7, flexShrink: 0, whiteSpace: "nowrap" }}>
          <ClipboardList size={13} /> Submission Pack
        </button>
      </div>

      {/* Progress bar + stats */}
      <div style={{ background: boxBg, border: `1px solid ${boxBorder}`, borderRadius: 12, padding: "12px 16px", marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: bodyCol }}>Overall completion</span>
          <span style={{ fontSize: 18, fontWeight: 700, color: headingCol, fontFamily: "'DM Serif Display',serif" }}>{pct}%</span>
        </div>
        <div style={{ height: 5, background: progressTrack, borderRadius: 3, overflow: "hidden", marginBottom: 8 }}>
          <div style={{ width: `${pct}%`, height: "100%", background: `linear-gradient(90deg,${sp.color},#2FAF9F)`, borderRadius: 3, transition: "width .8s" }} />
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {[{ l: "Complete", v: complete, c: "#10b981" }, { l: "Partial", v: partial, c: "#f59e0b" }, { l: "Missing", v: missing, c: "#7a9ab8" }, { l: "Req. outstanding", v: reqMiss, c: "#ef4444" }].map(s => (
            <span key={s.l} style={{ fontSize: 11, color: bodyCol }}><strong style={{ color: s.c }}>{s.v}</strong> {s.l}</span>
          ))}
        </div>
      </div>

      {/* Deadline alert banner */}
      {showBanner && (
        <div style={{ borderRadius: 10, border: `1px solid ${expiredDl.length ? "rgba(239,68,68,.35)" : "rgba(245,158,11,.35)"}`, background: expiredDl.length ? "rgba(239,68,68,.07)" : "rgba(245,158,11,.07)", padding: "11px 16px", marginBottom: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {expiredDl.length > 0 && (
                <span style={{ fontSize: 12.5, fontWeight: 700, color: "#ef4444", display: "flex", alignItems: "center", gap: 5 }}>
                  <AlertCircle size={13} />
                  {expiredDl.length} certificate{expiredDl.length > 1 ? "s" : ""} expired — renew before submitting
                </span>
              )}
              {urgentDl.length > 0 && (
                <span style={{ fontSize: 12.5, fontWeight: 700, color: "#f59e0b", display: "flex", alignItems: "center", gap: 5 }}>
                  <Clock size={13} />
                  {urgentDl.length} renewal{urgentDl.length > 1 ? "s" : ""} due within 12 weeks
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 7, flexWrap: "wrap" }}>
              {[...expiredDl, ...urgentDl].slice(0, 4).map(d => {
                const u = getUrgency(d.expiry_date);
                return (
                  <span key={d.id} style={{ fontSize: 10.5, padding: "2px 8px", borderRadius: 20, background: u?.bgColor, color: u?.color, border: `1px solid ${u?.borderColor}`, fontWeight: 600 }}>
                    {d.expiry_label || d.item_id} · {u?.shortLabel}
                  </span>
                );
              })}
              {expiredDl.length + urgentDl.length > 4 && (
                <span style={{ fontSize: 10.5, color: bodyCol }}>+{expiredDl.length + urgentDl.length - 4} more</span>
              )}
            </div>
          </div>
          <button onClick={onNavToDeadlines} style={{ padding: "7px 14px", borderRadius: 8, border: `1px solid ${expiredDl.length ? "rgba(239,68,68,.4)" : "rgba(245,158,11,.4)"}`, background: "transparent", color: expiredDl.length ? "#ef4444" : "#f59e0b", cursor: "pointer", fontSize: 11.5, fontWeight: 700, fontFamily: "inherit", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 5 }}>
            View deadlines <ChevronRight size={12} />
          </button>
        </div>
      )}

      {/* Bulk upload */}
      <BulkUploadPanel specialty={specialty} evidence={evidence} onBulkUpload={onBulkUpload} themeMode={themeMode} />

      {/* Filter bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {filterBtns.map(b => (
            <button key={b.key} onClick={() => setFilter(b.key)} style={{ padding: "5px 12px", borderRadius: 20, border: `1px solid ${filter === b.key ? b.color + "60" : boxBorder}`, background: filter === b.key ? b.color + "14" : "transparent", color: filter === b.key ? b.color : bodyCol, cursor: "pointer", fontSize: 11, fontWeight: filter === b.key ? 700 : 500, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5, transition: "all .12s" }}>
              {b.label} <span style={{ fontSize: 9.5, fontWeight: 700, padding: "0 4px", borderRadius: 8, background: filter === b.key ? b.color : "transparent", color: filter === b.key ? "#fff" : b.color }}>{b.count}</span>
            </button>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
          <input
            type="text"
            placeholder="Search items…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding: "5px 10px", borderRadius: 7, border: `1px solid ${boxBorder}`, background: boxBg, color: headingCol, fontFamily: "inherit", fontSize: 11.5, outline: "none", width: 160 }}
          />
          <button onClick={() => setReqOnly(x => !x)} style={{ padding: "5px 10px", borderRadius: 7, border: `1px solid ${reqOnly ? "#ef444460" : boxBorder}`, background: reqOnly ? "rgba(239,68,68,.1)" : "transparent", color: reqOnly ? "#ef4444" : bodyCol, cursor: "pointer", fontSize: 11, fontFamily: "inherit", fontWeight: reqOnly ? 700 : 500, whiteSpace: "nowrap" }}>
            Required only
          </button>
        </div>
      </div>

      {/* Domain-grouped item list */}
      {groupedByDomain.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: bodyCol }}>
          <CheckCircle size={28} style={{ margin: "0 auto 8px", color: "#10b981" }} />
          <div style={{ fontSize: 13, fontWeight: 600, color: headingCol }}>No items match this filter</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {groupedByDomain.map(({ domain, items }) => (
            <div key={domain.id}>
              {/* Domain header — click to open domain panel */}
              <div
                onClick={() => onNav(domain.id)}
                style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, cursor: "pointer", padding: "4px 2px" }}
              >
                <domain.icon size={12} style={{ color: domain.color, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: domain.color, letterSpacing: ".04em" }}>{DOMAIN_NUM[domain.id]}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: headingCol }}>{domain.label.replace(/DOM \d+ — /, "")}</span>
                <span style={{ fontSize: 10, color: bodyCol, marginLeft: 2 }}>
                  {domain.items.filter(i => evidence[i.id]?.status === "complete").length}/{domain.items.length} complete
                </span>
                <ChevronRight size={11} style={{ color: bodyCol, marginLeft: "auto" }} />
              </div>
              {/* Items */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingLeft: 20 }}>
                {items.map(item => {
                  const stColor = item.status === "complete" ? "#10b981" : item.status === "partial" ? "#f59e0b" : "#7a9ab8";
                  const StIcon = item.status === "complete" ? CheckCircle : item.status === "partial" ? Clock : AlertCircle;
                  const itemDl = deadlineByItemId[item.id];
                  const dlUrgency = itemDl ? getUrgency(itemDl.expiry_date) : null;
                  const rowBorder = dlUrgency && dlUrgency.level !== "ok" ? dlUrgency.borderColor : boxBorder;
                  return (
                    <div key={item.id} onClick={() => onNav(item.domain.id)}
                      style={{ border: `1px solid ${rowBorder}`, borderRadius: 9, padding: "9px 13px", background: dlUrgency && dlUrgency.level !== "ok" ? dlUrgency.bgColor : boxBg, display: "flex", alignItems: "flex-start", gap: 9, cursor: "pointer", transition: "all .12s" }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = domain.color + "45"; e.currentTarget.style.background = isLight ? "#F8FAFC" : "#1b3355"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = rowBorder; e.currentTarget.style.background = dlUrgency && dlUrgency.level !== "ok" ? dlUrgency.bgColor : boxBg; }}>
                      <StIcon size={13} style={{ color: stColor, flexShrink: 0, marginTop: 2 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 12.5, fontWeight: 600, color: headingCol }}>{item.label}</span>
                          {item.required && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: isLight ? "rgba(239,68,68,.1)" : "rgba(239,68,68,.14)", color: isLight ? "#dc2626" : "#fca5a5" }}>REQ</span>}
                        </div>
                        {item.ev.suggestedFilename && (
                          <div style={{ fontSize: 10, color: isLight ? "#64748B" : "#4a6e99", marginTop: 2, fontFamily: "monospace" }}>→ {item.ev.suggestedFilename}</div>
                        )}
                        {!item.ev.fileName && item.status === "missing" && (
                          <div style={{ fontSize: 10.5, color: bodyCol, marginTop: 2 }}>No evidence uploaded yet</div>
                        )}
                        {item.ev.analysis?.recommendation && item.status !== "complete" && (
                          <div style={{ fontSize: 10.5, color: isLight ? "#7a9ab8" : "#7a9ab8", marginTop: 2 }}>{item.ev.analysis.recommendation.slice(0, 120)}{item.ev.analysis.recommendation.length > 120 ? "…" : ""}</div>
                        )}
                        {dlUrgency && (
                          <div style={{ fontSize: 10, color: dlUrgency.color, marginTop: 3, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                            <CalendarClock size={10} /> {dlUrgency.label}
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                        <span style={{ fontSize: 9.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: item.status === "complete" ? "rgba(16,185,129,.1)" : item.status === "partial" ? "rgba(245,158,11,.1)" : "rgba(71,85,105,.1)", color: stColor, border: `1px solid ${stColor}25`, whiteSpace: "nowrap" }}>
                          {item.status === "complete" ? "Complete" : item.status === "partial" ? "Partial" : "Missing"}
                        </span>
                        {dlUrgency && (
                          <span style={{ fontSize: 9.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: dlUrgency.bgColor, color: dlUrgency.color, border: `1px solid ${dlUrgency.borderColor}`, whiteSpace: "nowrap" }}>
                            {dlUrgency.shortLabel}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



// ─── Specialty Selector ────────────────────────────────────────────
function SpecialtySelector({ onSelect, lastUsed, evidenceStore }) {
  return (
    <div style={{ minHeight: "100vh", background: "#0F1F2E", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32 }}>
        <div style={{ marginBottom: 24 }}>
          <CredentiaWordmark iconSize={48} />
        </div>
        <p style={{ fontSize: 13.5, color: "#64748B", maxWidth: 400, margin: "0 auto", lineHeight: 1.7 }}>Select your specialty to load evidence requirements, GMC filename conventions, and AI-powered submission pack generation.</p>

      {/* Continue banner if a specialty was previously used */}
      {lastUsed && SPECIALTIES[lastUsed] && (() => {
        const lu = SPECIALTIES[lastUsed];
        const ev = evidenceStore[lastUsed] || {};
        const allItems = lu.domains.flatMap(d => d.items);
        const done = allItems.filter(i => ev[i.id]?.status === "complete").length;
        const pct = Math.round((done / allItems.length) * 100);
        return (
          <div onClick={() => onSelect(lastUsed)}
            style={{ width: "100%", maxWidth: 660, marginBottom: 20, background: `linear-gradient(135deg, ${lu.color}14, ${lu.color}08)`, border: `1px solid ${lu.color}40`, borderRadius: 12, padding: "14px 18px", cursor: "pointer", display: "flex", alignItems: "center", gap: 14, transition: "all .18s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = lu.color + "80"; e.currentTarget.style.transform = "translateY(-1px)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = lu.color + "40"; e.currentTarget.style.transform = ""; }}>
            <div style={{ fontSize: 24, flexShrink: 0 }}>{lu.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: lu.color, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 2 }}>Continue where you left off</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#F2F4F7" }}>{lu.name}</div>
              <div style={{ fontSize: 11, color: "#64748B", marginTop: 3 }}>{done}/{allItems.length} items complete · {pct}% done</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: lu.color, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>Continue <ChevronRight size={14} /></div>
          </div>
        );
      })()}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(290px,1fr))", gap: 14, width: "100%", maxWidth: 660 }}>
        {Object.values(SPECIALTIES).map(sp => {
          const ev = evidenceStore[sp.id] || {};
          const allItems = sp.domains.flatMap(d => d.items);
          const done = allItems.filter(i => ev[i.id]?.status === "complete").length;
          const hasProgress = done > 0;
          return (
            <div key={sp.id} onClick={() => onSelect(sp.id)}
              style={{ background: "#162e4a", border: `1px solid ${lastUsed === sp.id ? sp.color + "50" : "#224068"}`, borderRadius: 16, padding: 26, cursor: "pointer", transition: "all .2s", position: "relative", overflow: "hidden" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = sp.color + "70"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 12px 40px ${sp.color}15`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = lastUsed === sp.id ? sp.color + "50" : "#224068"; e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}>
              <div style={{ position: "absolute", top: 0, right: 0, width: 110, height: 110, background: `radial-gradient(circle, ${sp.color}10, transparent 70%)`, borderRadius: "0 16px 0 0" }} />
              {hasProgress && (
                <div style={{ position: "absolute", top: 12, right: 12, fontSize: 9.5, fontWeight: 700, padding: "2px 7px", borderRadius: 20, background: `${sp.color}20`, color: sp.color, border: `1px solid ${sp.color}35` }}>
                  {done}/{allItems.length} done
                </div>
              )}
              <div style={{ marginBottom: 12 }}>{renderSpecialtyIcon(sp, 30)}</div>
              <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 19, fontWeight: 700, color: "#fff", marginBottom: 3 }}>{sp.name}</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: sp.color, marginBottom: 9 }}>{sp.college}</div>
              <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.6, marginBottom: 16 }}>{sp.description}</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
                {sp.domains.map(d => (<span key={d.id} style={{ fontSize: 9.5, padding: "2px 7px", borderRadius: 20, background: `${d.color}14`, color: d.color, border: `1px solid ${d.color}22`, fontWeight: 600 }}>{d.label.split(" ")[0]}</span>))}
              </div>
              {hasProgress && (
                <div style={{ height: 3, background: "#224068", borderRadius: 2, overflow: "hidden", marginBottom: 10 }}>
                  <div style={{ width: `${Math.round((done/allItems.length)*100)}%`, height: "100%", background: sp.color, borderRadius: 2 }} />
                </div>
              )}
              <div style={{ fontSize: 12, color: sp.color, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>{hasProgress ? "Continue" : "Select"} <ChevronRight size={13} /></div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 28, fontSize: 11, color: "#7a9ab8", textAlign: "center" }}>More specialties coming soon — General Internal Medicine, Psychiatry, O&G, Radiology and more.</div>
      <div style={{ marginTop: 8, fontSize: 10, color: "#224068", textAlign: "center", letterSpacing: ".06em" }}>A Medberry product</div>
    </div>
  );
}

// ─── App Shell ────────────────────────────────────────────────────────────────
// ─── UserPage ──────────────────────────────────────────────────────────────────
function UserPage({ user, theme, onBack, specialty, onSwitch }) {
  const [fullName, setFullName]       = useState(user.user_metadata?.full_name || "");
  const [gmcNumber, setGmcNumber]     = useState(user.user_metadata?.gmc_number || "");
  const [newEmail, setNewEmail]       = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg]   = useState(null);
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailMsg, setEmailMsg]       = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting]       = useState(false);
  const [deleteMsg, setDeleteMsg]     = useState(null);

  // Sync displayed name from parent if auth state refreshes (e.g. after updateUser)
  useEffect(() => {
    if (user.user_metadata?.full_name) setFullName(user.user_metadata.full_name);
    if (user.user_metadata?.gmc_number) setGmcNumber(user.user_metadata.gmc_number);
  }, [user.user_metadata?.full_name, user.user_metadata?.gmc_number]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveProfile = async () => {
    setProfileSaving(true);
    setProfileMsg(null);
    const { error } = await supabase.auth.updateUser({ data: { full_name: fullName.trim(), gmc_number: gmcNumber.trim() } });
    setProfileSaving(false);
    if (error) setProfileMsg({ type: "error", text: error.message });
    else setProfileMsg({ type: "success", text: "Profile saved successfully." });
  };

  const changeEmail = async () => {
    if (!newEmail.trim()) return;
    setEmailSaving(true);
    setEmailMsg(null);
    const { error } = await supabase.auth.updateUser({ email: newEmail.trim() });
    setEmailSaving(false);
    if (error) setEmailMsg({ type: "error", text: error.message });
    else { setEmailMsg({ type: "success", text: "Confirmation emails sent to both addresses. Click the link to confirm the change." }); setNewEmail(""); }
  };

  const deleteAccount = async () => {
    if (deleteConfirm !== user.email) return;
    setDeleting(true);
    setDeleteMsg(null);
    try {
      const res = await apiFetch(`${API}/api/account/delete`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || "Account deletion failed.");
      await supabase.auth.signOut();
    } catch (e) {
      setDeleting(false);
      setDeleteMsg({ type: "error", text: e.message });
    }
  };

  const initials = fullName
    ? fullName.split(" ").filter(Boolean).map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : user.email[0].toUpperCase();

  const inp = (extra = {}) => ({
    width: "100%", background: theme.selectBg, border: `1px solid ${theme.cardBorder}`,
    borderRadius: 8, color: theme.appText, fontFamily: "inherit", fontSize: 13.5,
    padding: "10px 13px", outline: "none", transition: "border .15s",
    ...extra,
  });
  const lbl = { fontSize: 10.5, fontWeight: 700, color: theme.dim, letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 5, display: "block" };
  const card = { background: theme.panelBg, border: `1px solid ${theme.panelBorder}`, borderRadius: 12, padding: "22px 24px", marginBottom: 16 };
  const title = { fontSize: 13, fontWeight: 700, color: theme.appText, marginBottom: 14 };
  const msg = (type) => ({
    padding: "9px 13px", borderRadius: 7, fontSize: 12.5, marginTop: 12,
    background: type === "success" ? "rgba(16,185,129,.1)" : "rgba(239,68,68,.1)",
    border: `1px solid ${type === "success" ? "rgba(16,185,129,.25)" : "rgba(239,68,68,.25)"}`,
    color: type === "success" ? "#10b981" : "#f87171",
  });
  const btn = (variant = "primary", disabled = false) => ({
    padding: "9px 18px", borderRadius: 8, border: "none", cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: "inherit", fontSize: 13, fontWeight: 600, transition: "opacity .15s",
    background: variant === "danger" ? "#ef4444" : "#2FAF9F",
    color: "#fff", opacity: disabled ? 0.45 : 1,
  });

  return (
    <div style={{ maxWidth: 580 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
        <button
          onClick={onBack}
          style={{ background: "none", border: "none", cursor: "pointer", color: theme.muted, padding: "4px 2px", display: "flex", alignItems: "center", borderRadius: 6, transition: "color .15s" }}
          onMouseEnter={e => e.currentTarget.style.color = theme.appText}
          onMouseLeave={e => e.currentTarget.style.color = theme.muted}
          title="Back"
        >
          <ChevronLeft size={18} />
        </button>
        <h1 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 22, fontWeight: 700, color: theme.appText, margin: 0 }}>Account</h1>
      </div>

      {/* Avatar + identity */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 26 }}>
        <div style={{ width: 56, height: 56, borderRadius: "50%", background: "linear-gradient(135deg,#2FAF9F,#1a7a70)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700, color: "#fff", flexShrink: 0, fontFamily: "'DM Serif Display',serif" }}>
          {initials}
        </div>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: theme.appText, fontFamily: "'DM Serif Display',serif" }}>{fullName || <span style={{ color: theme.dim, fontStyle: "italic", fontFamily: "inherit" }}>Name not set</span>}</div>
          <div style={{ fontSize: 12, color: theme.muted, marginTop: 2 }}>{user.email}</div>
          {gmcNumber && <div style={{ fontSize: 11, color: theme.dim, marginTop: 1 }}>GMC: {gmcNumber}</div>}
        </div>
      </div>

      {/* Profile */}
      <div style={card}>
        <div style={title}>Profile</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={lbl}>Full Name</label>
            <input style={inp()} placeholder="e.g. Dr Jane Smith" value={fullName} onChange={e => { setFullName(e.target.value); setProfileMsg(null); }} />
          </div>
          <div>
            <label style={lbl}>GMC Number</label>
            <input style={inp()} placeholder="e.g. 1234567" value={gmcNumber} onChange={e => { setGmcNumber(e.target.value.replace(/\D/g, "")); setProfileMsg(null); }} maxLength={7} />
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <button onClick={saveProfile} disabled={profileSaving} style={btn("primary", profileSaving)}>
            {profileSaving ? "Saving…" : "Save Profile"}
          </button>
        </div>
        {profileMsg && <div style={msg(profileMsg.type)}>{profileMsg.text}</div>}
      </div>

      {/* Specialty */}
      {specialty && SPECIALTIES[specialty] && (() => {
        const sp = SPECIALTIES[specialty];
        return (
          <div style={card}>
            <div style={title}>Specialty</div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 9, background: `${sp.color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {renderSpecialtyIcon(sp, 20)}
                </div>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: theme.appText }}>{sp.name}</div>
                  <div style={{ fontSize: 11, color: theme.muted, marginTop: 1 }}>{sp.college}</div>
                </div>
              </div>
              <button
                onClick={onSwitch}
                style={{ padding: "7px 14px", borderRadius: 8, border: `1px solid ${sp.color}45`, background: `${sp.color}10`, color: sp.color, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}
              >
                Switch specialty
              </button>
            </div>
          </div>
        );
      })()}

      {/* Subscription */}
      <div style={card}>
        <div style={title}>Subscription</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{ background: "rgba(47,175,159,.13)", border: "1px solid rgba(47,175,159,.3)", borderRadius: 6, padding: "2px 10px", fontSize: 11, fontWeight: 700, color: "#2FAF9F", letterSpacing: ".07em" }}>
            FREE BETA
          </div>
          <span style={{ fontSize: 12.5, color: theme.muted }}>Full access during beta period</span>
        </div>
        <div style={{ fontSize: 12, color: theme.dim, lineHeight: 1.7 }}>
          Subscription management will be available here once billing launches. You'll be notified before any changes.
        </div>
      </div>

      {/* Change email */}
      <div style={card}>
        <div style={title}>Change Email</div>
        <div style={{ fontSize: 12.5, color: theme.muted, marginBottom: 14 }}>
          Current: <strong style={{ color: theme.appText }}>{user.email}</strong>
        </div>
        <div>
          <label style={lbl}>New Email Address</label>
          <input style={inp()} type="email" placeholder="new@email.com" value={newEmail} onChange={e => { setNewEmail(e.target.value); setEmailMsg(null); }} />
        </div>
        <div style={{ marginTop: 12 }}>
          <button onClick={changeEmail} disabled={emailSaving || !newEmail.trim()} style={btn("primary", emailSaving || !newEmail.trim())}>
            {emailSaving ? "Sending…" : "Send Confirmation"}
          </button>
        </div>
        {emailMsg
          ? <div style={msg(emailMsg.type)}>{emailMsg.text}</div>
          : <div style={{ fontSize: 11.5, color: theme.dim, marginTop: 10 }}>A confirmation link will be sent to both your current and new addresses.</div>
        }
      </div>

      {/* Danger zone */}
      <div style={{ ...card, border: "1px solid rgba(239,68,68,.22)", background: "rgba(239,68,68,.025)", marginBottom: 0 }}>
        <div style={{ ...title, color: "#ef4444" }}>Danger Zone</div>
        <div style={{ fontSize: 12.5, color: theme.muted, marginBottom: 14, lineHeight: 1.65 }}>
          Permanently deletes your account and all associated evidence data. This <strong>cannot be undone</strong>.
        </div>
        <div>
          <label style={lbl}>Type your email to confirm: <span style={{ color: theme.muted, textTransform: "none", fontWeight: 400, letterSpacing: 0 }}>{user.email}</span></label>
          <input
            style={inp({ borderColor: deleteConfirm && deleteConfirm !== user.email ? "rgba(239,68,68,.5)" : undefined })}
            placeholder={user.email}
            value={deleteConfirm}
            onChange={e => { setDeleteConfirm(e.target.value); setDeleteMsg(null); }}
          />
        </div>
        <div style={{ marginTop: 12 }}>
          <button onClick={deleteAccount} disabled={deleting || deleteConfirm !== user.email} style={btn("danger", deleting || deleteConfirm !== user.email)}>
            {deleting ? "Deleting…" : "Delete My Account"}
          </button>
        </div>
        {deleteMsg && <div style={msg(deleteMsg.type)}>{deleteMsg.text}</div>}
      </div>
    </div>
  );
}
// ──────────────────────────────────────────────────────────────────────────────

// ── Allowed emails for the full portfolio tracker ─────────────────────────────
// Add email addresses here to grant access to /app.
const ALLOWED_EMAILS = [
  "rob@medberry.co.uk",
  "rob@wildmedic.co.uk",
  "bnv3479@gmail.com",
  // add more emails here as needed
];

function AccessDenied({ email }) {
  return (
    <div style={{
      minHeight: "100vh", background: "#0F1F2E",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'Inter','Segoe UI',sans-serif", padding: 24,
    }}>
      <div style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 20 }}>🔒</div>
        <h2 style={{ color: "#F2F4F7", fontSize: 20, fontWeight: 700, marginBottom: 12, fontFamily: "Georgia,serif" }}>
          Access restricted
        </h2>
        <p style={{ color: "#64748B", fontSize: 13.5, lineHeight: 1.7, marginBottom: 8 }}>
          <strong style={{ color: "#7a9ab8" }}>{email}</strong> does not have access to the Credentia portfolio tracker.
        </p>
        <p style={{ color: "#64748B", fontSize: 13, lineHeight: 1.7, marginBottom: 28 }}>
          The portfolio tracker is currently invite-only.{" "}
          <a href="mailto:hello@credentia.pro" style={{ color: "#2FAF9F", textDecoration: "none" }}>
            Contact us
          </a>{" "}
          to request access.
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <a href="/cv" style={{
            background: "#2FAF9F", color: "#fff", padding: "10px 22px", borderRadius: 9,
            textDecoration: "none", fontWeight: 600, fontSize: 13.5,
          }}>
            Try the free CV analyser
          </a>
          <button
            onClick={() => supabase.auth.signOut()}
            style={{
              background: "none", border: "1px solid #1e3048", color: "#64748B",
              padding: "10px 22px", borderRadius: 9, cursor: "pointer", fontSize: 13.5,
              fontFamily: "inherit",
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(undefined); // undefined = loading, null = signed out

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => setSession(session));
    return () => subscription.unsubscribe();
  }, []);

  if (session === undefined) {
    return (
      <div style={{ minHeight: "100vh", background: "#0F1F2E", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 28, height: 28, border: "3px solid #213045", borderTopColor: "#2FAF9F", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (!session) return <AuthScreen />;

  const email = session.user?.email?.toLowerCase() ?? "";
  if (!ALLOWED_EMAILS.map(e => e.toLowerCase()).includes(email)) {
    return <AccessDenied email={session.user?.email} />;
  }

  return <AppShell user={session.user} />;
}

function AppShell({ user }) {
  // Scope all localStorage keys to this user so different accounts on the same
  // browser never bleed into each other
  const uid = user.id.replace(/-/g, "").slice(0, 12);
  const LS_SP  = `cesr_sp4_${uid}`;
  const LS_EV  = `cesr_ev4_${uid}`;
  const LS_LU  = `cesr_lastused_${uid}`;

  const [specialty, setSpecialty]   = useLSState(LS_SP, null);
  const [view, setView]             = useState("dashboard");
  const [showPack, setShowPack]     = useState(false);

  // ── Evidence store: localStorage for instant load, Supabase for persistence ──
  const [evidenceStore, setEvidenceStoreRaw] = useState(() => {
    try { const v = localStorage.getItem(LS_EV); return v ? JSON.parse(v) : {}; } catch { return {}; }
  });
  const [syncStatus, setSyncStatus] = useState("loading"); // loading | saved | saving | offline
  const syncTimer = useRef(null);

  // Load from Supabase on mount — cloud data wins over stale localStorage
  useEffect(() => {
    supabase.from("user_evidence").select("specialty, evidence_json").eq("user_id", user.id)
      .then(({ data, error }) => {
        if (error) { setSyncStatus("offline"); return; }
        if (data?.length) {
          setEvidenceStoreRaw(prev => {
            const merged = { ...prev };
            data.forEach(r => { merged[r.specialty] = r.evidence_json; });
            try { localStorage.setItem(LS_EV, JSON.stringify(merged)); } catch {}
            return merged;
          });
        }
        setSyncStatus("saved");
      });
  }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setEvidenceStore = newStore => {
    setEvidenceStoreRaw(newStore);
    try { localStorage.setItem(LS_EV, JSON.stringify(newStore)); } catch {}
    setSyncStatus("saving");
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(async () => {
      const rows = Object.entries(newStore).map(([sp, evidence_json]) => ({
        user_id: user.id, specialty: sp, evidence_json, updated_at: new Date().toISOString(),
      }));
      if (!rows.length) { setSyncStatus("saved"); return; }
      const { error } = await supabase.from("user_evidence").upsert(rows, { onConflict: "user_id,specialty" });
      setSyncStatus(error ? "offline" : "saved");
    }, 2000);
  };
  // ─────────────────────────────────────────────────────────────────────────────

  const evidence    = specialty ? (evidenceStore[specialty] || initEvidence(specialty)) : {};
  const updEvidence = (id, updates) => { const curr = evidenceStore[specialty] || initEvidence(specialty); setEvidenceStore({ ...evidenceStore, [specialty]: { ...curr, [id]: { ...curr[id], ...updates } } }); };
  // Accepts pre-confirmed [{file, item}] assignments from the review modal
  const handleBulkUpload = async (assignments, onProgress) => {
    if (!specialty) return { analysed: 0, failed: [], errors: {} };
    const workingEvidence = { ...(evidenceStore[specialty] || initEvidence(specialty)) };
    let analysed = 0;
    const failed = [];
    const errors = {}; // fileName -> error message for display
    for (let i = 0; i < assignments.length; i++) {
      const { file, item } = assignments[i];
      try {
        const result = await analyseEvidenceFile({ specialtyId: specialty, domainId: item.domainId, item, file });
        workingEvidence[item.id] = { ...(workingEvidence[item.id] || {}), ...result };
        analysed += 1;
      } catch (e) {
        console.error("[Credentia] Bulk analysis error for", file.name, ":", e);
        failed.push(file.name);
        errors[file.name] = e?.message || "Unknown error";
      }
      onProgress?.(i + 1);
    }
    setEvidenceStore({ ...evidenceStore, [specialty]: workingEvidence });
    return { analysed, failed, errors };
  };
  const handleSelect = sp => { setSpecialty(sp); if (!evidenceStore[sp]) setEvidenceStore({ ...evidenceStore, [sp]: initEvidence(sp) }); setView("dashboard"); };
  const handleSwitch = () => { setSpecialty(null); setView("dashboard"); };

  const [lastUsed, setLastUsed] = useLSState(LS_LU, null);
  const [uiTheme, setUiThemeRaw] = useLSState(`cesr_theme_${uid}`, "clinical_light");
  const validTheme = UI_THEMES[uiTheme] ? uiTheme : "clinical_light";
  const setUiTheme = v => setUiThemeRaw(v);
  const [cvFileData, setCvFileData] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const doSelect = sp => { setLastUsed(sp); handleSelect(sp); };
  const navTo = v => { setView(v); setSidebarOpen(false); };

  // ── Deadlines state ──────────────────────────────────────────────────────────
  const [deadlines, setDeadlines] = useState([]);

  useEffect(() => {
    if (!specialty) return;
    supabase.from("deadlines")
      .select("*")
      .eq("user_id", user.id)
      .eq("specialty", specialty)
      .neq("status", "removed")
      .then(({ data }) => { setDeadlines(data || []); });
  }, [user.id, specialty]); // eslint-disable-line react-hooks/exhaustive-deps

  const createOrUpdateDeadline = useCallback(async (itemId, domainId, expiryInfo) => {
    const sp = SPECIALTIES[specialty];
    if (!sp) return;
    const domain = sp.domains.find(d => d.id === domainId);
    const item = domain?.items.find(i => i.id === itemId);
    if (!domain || !item) return;

    const reminderDates = computeReminderDates(expiryInfo.expiryDate);
    const renewalLink = findRenewalLink(item.label);

    // Determine which reminders to keep based on user preference
    const opt = expiryInfo.reminderOption || "all";
    const skipReminders = {
      reminder_12w_sent: opt === "6w" || opt === "2w" ? true : reminderDates.reminder_12w_sent,
      reminder_6w_sent:  opt === "2w"                 ? true : reminderDates.reminder_6w_sent,
      reminder_2w_sent:  opt === "12w" || opt === "6w" ? true : reminderDates.reminder_2w_sent,
    };

    const record = {
      user_id: user.id,
      specialty,
      domain_id: domainId,
      domain_label: domain.label,
      item_id: itemId,
      item_label: item.label,
      expiry_label: expiryInfo.expiryLabel || (item.label + " expiry"),
      expiry_type: expiryInfo.expiryType || null,
      expiry_date: expiryInfo.expiryDate,
      renewal_lead_time_weeks: expiryInfo.renewalLeadTime || 6,
      notes: expiryInfo.notes || null,
      status: "active",
      source: expiryInfo.source || "manual",
      ai_confidence: expiryInfo.confidence || null,
      renewal_url: expiryInfo.renewalUrl || renewalLink?.url || null,
      // GMC currency fields
      evidence_date: expiryInfo.evidenceDate || null,
      evidence_date_confidence: expiryInfo.evidenceDateConfidence || null,
      evidence_date_note: expiryInfo.evidenceDateNote || null,
      evidence_validity_rule: expiryInfo.evidenceValidityRule || null,
      gmc_currency_expiry: expiryInfo.gmcCurrencyExpiry || null,
      reminder_12w_date: reminderDates.reminder_12w_date,
      reminder_6w_date:  reminderDates.reminder_6w_date,
      reminder_2w_date:  reminderDates.reminder_2w_date,
      ...skipReminders,
      updated_at: new Date().toISOString(),
    };

    const existing = deadlines.find(d => d.item_id === itemId && d.status === "active");
    if (existing) {
      const { data, error } = await supabase.from("deadlines").update(record).eq("id", existing.id).select().single();
      if (!error && data) setDeadlines(prev => prev.map(d => d.id === existing.id ? data : d));
    } else {
      const { data, error } = await supabase.from("deadlines").insert(record).select().single();
      if (!error && data) setDeadlines(prev => [...prev, data]);
    }
  }, [user.id, specialty, deadlines]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeDeadline = useCallback(async (deadlineId) => {
    await supabase.from("deadlines").update({ status: "removed" }).eq("id", deadlineId);
    setDeadlines(prev => prev.filter(d => d.id !== deadlineId));
  }, []);

  const markDeadlineRenewed = useCallback(async (deadlineId) => {
    const { data } = await supabase.from("deadlines").update({ status: "renewed", updated_at: new Date().toISOString() }).eq("id", deadlineId).select().single();
    if (data) setDeadlines(prev => prev.map(d => d.id === deadlineId ? data : d));
  }, []);
  // ────────────────────────────────────────────────────────────────────────────

  if (!specialty) return (<><style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=DM+Serif+Display&display=swap');*{box-sizing:border-box;margin:0;padding:0}`}</style><SpecialtySelector onSelect={doSelect} lastUsed={lastUsed} evidenceStore={evidenceStore} /></>);

  const sp = SPECIALTIES[specialty];
  const theme = UI_THEMES[validTheme];
  const allItems = sp.domains.flatMap(d => d.items);
  const complete = allItems.filter(i => evidence[i.id]?.status === "complete").length;
  const pct      = Math.round((complete / allItems.length) * 100);
  const missing  = allItems.filter(i => evidence[i.id]?.status === "missing").length;

  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif", background: theme.appBg, minHeight: "100vh", color: theme.appText, display: "flex", transition: "background .2s ease, color .2s ease" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=DM+Serif+Display&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:${theme.appBg}}::-webkit-scrollbar-thumb{background:${theme.sidebarBorder};border-radius:2px}
        .ni{cursor:pointer;padding:7px 11px;border-radius:7px;display:flex;align-items:center;gap:8px;font-size:11.5px;font-weight:500;transition:all .12s;color:${theme.navText}}
        .ni:hover{background:${theme.navHoverBg};color:${theme.navHoverText}}.ni.active{background:${theme.navActiveBg};color:${theme.navActiveText}}
        @keyframes spin{to{transform:rotate(360deg)}}
        .app-sidebar{transition:transform .25s ease,background .2s ease,border-color .2s ease}
        .mob-bar{display:none;position:fixed;top:0;left:0;right:0;height:48px;background:${theme.sidebarBg};border-bottom:1px solid ${theme.sidebarBorder};z-index:8;align-items:center;padding:0 14px;gap:10px;transition:background .2s ease,border-color .2s ease}
        .mob-bar-btn{background:none;border:none;cursor:pointer;color:${theme.appText};padding:6px;display:flex;align-items:center;border-radius:7px;transition:background .12s}
        .mob-bar-btn:hover{background:${theme.navHoverBg}}
        .sidebar-backdrop{display:none;position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:9;cursor:pointer}
        @media(max-width:768px){
          .mob-bar{display:flex}
          .app-sidebar{transform:translateX(-212px)}
          .app-sidebar.open{transform:translateX(0)}
          .sidebar-backdrop.open{display:block}
          .main-content{margin-left:0!important;padding-top:68px!important}
        }
      `}</style>

      {showPack && <SubmissionPackModal specialty={specialty} evidence={evidence} onClose={() => setShowPack(false)} user={user} />}

      {/* Mobile top bar */}
      <div className="mob-bar">
        <button className="mob-bar-btn" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
          <Menu size={20} />
        </button>
        <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 14, fontWeight: 700, color: theme.appText, letterSpacing: ".04em" }}>CREDENTIA</div>
        <div style={{ marginLeft: "auto", fontSize: 9.5, color: theme.muted, display: "flex", alignItems: "center", gap: 5 }}>
          {renderSpecialtyIcon(sp, 12)} {sp.name}
        </div>
      </div>

      {/* Backdrop for mobile sidebar */}
      <div className={`sidebar-backdrop${sidebarOpen ? " open" : ""}`} onClick={() => setSidebarOpen(false)} />

      <aside className={`app-sidebar${sidebarOpen ? " open" : ""}`} style={{ width: 212, flexShrink: 0, background: theme.sidebarBg, borderRight: `1px solid ${theme.sidebarBorder}`, display: "flex", flexDirection: "column", padding: "13px 8px", position: "fixed", top: 0, bottom: 0, left: 0, overflowY: "auto", zIndex: 10 }}>
        <div style={{ padding: "4px 9px 13px", borderBottom: `1px solid ${theme.sidebarBorder}`, marginBottom: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
            <CredentiaIcon size={22} />
            <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 14, fontWeight: 700, color: theme.appText, letterSpacing: ".04em" }}>CREDENTIA</div>
          </div>
          <div style={{ fontSize: 8.5, color: "#2FAF9F", fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 2 }}>Turn Evidence Into CESR</div>
          <div style={{ fontSize: 9.5, color: theme.muted, marginTop: 2, display: "flex", alignItems: "center", gap: 5 }}>
            {renderSpecialtyIcon(sp, 12)} {sp.name}
          </div>
          <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
            <span style={{ fontSize: 8.5, color: theme.appText, background: theme.navActiveBg, border: `1px solid ${theme.sidebarBorder}`, padding: "1px 6px", borderRadius: 999, letterSpacing: ".04em" }}>
              {BUILD_BADGE}
            </span>
            {syncStatus === "loading" && (
              <span style={{ fontSize: 8.5, color: theme.muted, display: "flex", alignItems: "center", gap: 3 }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", border: `1.5px solid ${theme.muted}`, borderTopColor: "transparent", display: "inline-block", animation: "spin .8s linear infinite" }} />
                loading…
              </span>
            )}
            {syncStatus === "saving" && (
              <span style={{ fontSize: 8.5, color: "#f59e0b", display: "flex", alignItems: "center", gap: 3 }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", border: "1.5px solid #f59e0b", borderTopColor: "transparent", display: "inline-block", animation: "spin .8s linear infinite" }} />
                saving…
              </span>
            )}
            {syncStatus === "saved" && (
              <span style={{ fontSize: 8.5, color: "#10b981" }}>✓ saved</span>
            )}
            {syncStatus === "offline" && (
              <span style={{ fontSize: 8.5, color: "#ef4444" }}>⚠ offline</span>
            )}
          </div>
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 8.5, color: theme.muted, marginBottom: 4, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }}>Theme</div>
            <select
              value={validTheme}
              onChange={e => setUiTheme(e.target.value)}
              style={{ width: "100%", background: theme.selectBg, border: `1px solid ${theme.selectBorder}`, borderRadius: 6, color: theme.appText, fontSize: 10.5, padding: "5px 6px", fontFamily: "inherit" }}
            >
              {Object.entries(UI_THEMES).map(([key, t]) => <option key={key} value={key}>{t.label}</option>)}
            </select>
          </div>
        </div>
        <div className={`ni${view === "dashboard" ? " active" : ""}`} onClick={() => navTo("dashboard")}>
          <TrendingUp size={12} />Portfolio
          {(() => { const reqM = sp.domains.flatMap(d => d.items).filter(i => i.required && (evidence[i.id]?.status || "missing") === "missing").length; return reqM > 0 ? <span style={{ marginLeft: "auto", background: "#ef4444", color: "#fff", borderRadius: 9, fontSize: 9, fontWeight: 700, padding: "1px 5px" }}>{reqM}</span> : null; })()}
        </div>
        <div className="ni" onClick={() => { setShowPack(true); setSidebarOpen(false); }} style={{ color: sp.color }}><ClipboardList size={12} />Submission Pack</div>

        {/* Sidebar domain list — grouped for anaesthetics, flat for EM */}
        {(() => {
          const anaGroups = specialty === "anaesthetics" ? [
            { label: "Foundation", ids: ["cv","qualifications","work_exp","appraisals"] },
            { label: "Generic Domains", ids: ["dom1","dom2","dom3","dom4","dom5","dom6","dom7","dom8"] },
            { label: "Clinical Domains", ids: ["dom9","dom10","dom11","dom12","dom13","dom14"] },
            { label: "Additional", ids: ["practical","sia","verification"] },
          ] : null;

          const DomainItem = ({ d }) => {
            const Icon = d.icon;
            const comp = d.items.filter(i => evidence[i.id]?.status === "complete").length;
            const reqM = d.items.filter(i => i.required && evidence[i.id]?.status === "missing").length;
            const dp = Math.round((comp / d.items.length) * 100);
            return (
              <div className={`ni${view === d.id ? " active" : ""}`} onClick={() => navTo(d.id)}>
                <Icon size={12} style={{ color: view === d.id ? d.color : undefined, flexShrink: 0 }} />
                <span style={{ flex: 1, lineHeight: 1.3, fontSize: 10.5 }}>{d.label.replace(/DOM \d+ — /,"").split("&")[0].split("/")[0].trim()}</span>
                <span style={{ fontSize: 8.5, color: theme.dim, fontFamily: "monospace" }}>{DOMAIN_NUM[d.id]}</span>
                {reqM > 0 ? <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#ef4444", flexShrink: 0 }} /> : dp === 100 ? <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#10b981", flexShrink: 0 }} /> : null}
              </div>
            );
          };

          if (anaGroups) {
            return anaGroups.map(group => {
              const groupDomains = group.ids.map(id => sp.domains.find(d => d.id === id)).filter(Boolean);
              return (
                <div key={group.label}>
                  <div style={{ padding: "8px 10px 2px", fontSize: 8.5, fontWeight: 700, color: theme.dim, letterSpacing: ".1em", textTransform: "uppercase", marginTop: 2 }}>{group.label}</div>
                  {groupDomains.map(d => <DomainItem key={d.id} d={d} />)}
                </div>
              );
            });
          }
          return (
            <>
              <div style={{ padding: "9px 10px 3px", fontSize: 9, fontWeight: 700, color: theme.dim, letterSpacing: ".12em", textTransform: "uppercase", marginTop: 3 }}>Domains</div>
              {sp.domains.map(d => <DomainItem key={d.id} d={d} />)}
            </>
          );
        })()}
        {/* Deadlines nav item */}
        {(() => {
          const expiredCount = deadlines.filter(d => {
            if (d.status !== "active") return false;
            const u = getUrgency(d.expiry_date);
            return u?.level === "expired" || u?.level === "critical";
          }).length;
          const urgentCount = deadlines.filter(d => {
            if (d.status !== "active") return false;
            const u = getUrgency(d.expiry_date);
            return u?.level === "urgent" || u?.level === "soon";
          }).length;
          const badgeCount = expiredCount + urgentCount;
          const badgeColor = expiredCount > 0 ? "#ef4444" : "#f59e0b";
          return (
            <div className={`ni${view === "deadlines" ? " active" : ""}`} onClick={() => navTo("deadlines")}>
              <CalendarClock size={12} style={{ color: view === "deadlines" ? "#2FAF9F" : undefined, flexShrink: 0 }} />
              <span style={{ flex: 1 }}>Deadlines</span>
              {badgeCount > 0 && (
                <span style={{ marginLeft: "auto", background: badgeColor, color: "#fff", borderRadius: 9, fontSize: 9, fontWeight: 700, padding: "1px 5px", flexShrink: 0 }}>{badgeCount}</span>
              )}
            </div>
          );
        })()}

        <div style={{ marginTop: "auto", padding: "11px 4px 3px" }}>
          <div style={{ background: theme.panelBg, borderRadius: 8, padding: "9px 10px", border: `1px solid ${theme.panelBorder}`, marginBottom: 6 }}>
            <div style={{ fontSize: 9, color: theme.dim, marginBottom: 3, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase" }}>Progress</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: theme.appText, fontFamily: "'DM Serif Display',serif" }}>{pct}<span style={{ fontSize: 10, color: theme.muted }}>%</span></div>
            <div style={{ height: 3, background: theme.cardBorder, borderRadius: 2, marginTop: 4, overflow: "hidden" }}><div style={{ height: "100%", background: `linear-gradient(90deg,${sp.color},#2FAF9F)`, borderRadius: 2, width: `${pct}%`, transition: "width .6s" }} /></div>
            {missing > 0 && <div style={{ fontSize: 9.5, color: theme.muted, marginTop: 4 }}>{missing} outstanding</div>}
          </div>
          {sp.iconAttribution && (
            <div style={{ padding: "0 2px 6px", fontSize: 8.5, color: theme.dim, lineHeight: 1.5 }}>
              <a href={sp.iconAttribution.href} title={sp.iconAttribution.title} target="_blank" rel="noreferrer" style={{ color: theme.dim, textDecoration: "none" }}>
                {sp.iconAttribution.text}
              </a>
            </div>
          )}
          <div
            onClick={() => navTo("account")}
            style={{ background: view === "account" ? theme.navActiveBg : theme.cardBg, borderRadius: 8, padding: "8px 10px", border: `1px solid ${view === "account" ? theme.sidebarBorder : theme.cardBorder}`, display: "flex", alignItems: "center", gap: 6, cursor: "pointer", transition: "background .12s,border-color .12s" }}
            onMouseEnter={e => { if (view !== "account") e.currentTarget.style.background = theme.navHoverBg; }}
            onMouseLeave={e => { if (view !== "account") e.currentTarget.style.background = theme.cardBg; }}
          >
            <div style={{ width: 24, height: 24, borderRadius: "50%", background: "linear-gradient(135deg,#2FAF9F,#1a7a70)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 9.5, fontWeight: 700, color: "#fff" }}>
              {user.user_metadata?.full_name
                ? user.user_metadata.full_name.split(" ").filter(Boolean).map(n => n[0]).join("").toUpperCase().slice(0, 2)
                : <User size={11} />
              }
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {user.user_metadata?.full_name
                ? <div style={{ fontSize: 10.5, color: theme.appText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 500 }}>{user.user_metadata.full_name}</div>
                : null
              }
              <div style={{ fontSize: 9.5, color: theme.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</div>
            </div>
            <button
              onClick={e => { e.stopPropagation(); supabase.auth.signOut(); }}
              title="Sign out"
              style={{ background: "none", border: "none", cursor: "pointer", color: theme.muted, padding: 4, flexShrink: 0, display: "flex", alignItems: "center", borderRadius: 5, transition: "color .15s" }}
              onMouseEnter={e => e.currentTarget.style.color = "#ef4444"}
              onMouseLeave={e => e.currentTarget.style.color = theme.muted}
            >
              <LogOut size={13} />
            </button>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", padding: "8px 2px 2px" }}>
            <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ fontSize: 9, color: theme.dim, textDecoration: "none" }} onMouseEnter={e => e.currentTarget.style.color = "#2FAF9F"} onMouseLeave={e => e.currentTarget.style.color = theme.dim}>Terms</a>
            <span style={{ fontSize: 9, color: theme.dim }}>·</span>
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ fontSize: 9, color: theme.dim, textDecoration: "none" }} onMouseEnter={e => e.currentTarget.style.color = "#2FAF9F"} onMouseLeave={e => e.currentTarget.style.color = theme.dim}>Privacy</a>
          </div>
        </div>
      </aside>

      <main className="main-content" style={{ flex: 1, marginLeft: 212, padding: "26px 30px", minHeight: "100vh" }}>
        {view === "account" && <UserPage user={user} theme={theme} onBack={() => navTo("dashboard")} specialty={specialty} onSwitch={handleSwitch} />}
        {(view === "dashboard" || view === "checklist") && <Dashboard evidence={evidence} specialty={specialty} onNav={setView} onSwitch={handleSwitch} onPack={() => setShowPack(true)} onBulkUpload={handleBulkUpload} themeMode={validTheme} deadlines={deadlines} onNavToDeadlines={() => navTo("deadlines")} />}
        {view === "deadlines" && (
          <DeadlinesView
            deadlines={deadlines}
            specialty={specialty}
            specialtyData={sp}
            evidence={evidence}
            user={user}
            themeMode={validTheme}
            onCreateDeadline={createOrUpdateDeadline}
            onRemoveDeadline={removeDeadline}
            onMarkRenewed={markDeadlineRenewed}
            onNavToItem={domainId => navTo(domainId)}
          />
        )}
        {view !== "account" && view !== "deadlines" && sp.domains.find(d => d.id === view) && (
          <DomainPanel
            domain={sp.domains.find(d => d.id === view)}
            evidence={evidence}
            onUpdate={updEvidence}
            specialtyId={specialty}
            themeMode={validTheme}
            onCvFileData={view === "cv" ? setCvFileData : undefined}
            onBulkUpload={handleBulkUpload}
            deadlines={deadlines}
            onSetDeadline={createOrUpdateDeadline}
            onRemoveDeadline={removeDeadline}
          />
        )}
        {view !== "account" && view === "cv" && <CVAnalyser specialty={specialty} evidence={evidence} onUpdate={updEvidence} themeMode={validTheme} cvFileData={cvFileData} />}
      </main>
    </div>
  );
}
